import React, { useState, useEffect, useContext, createContext } from 'react';

// Classifications are managed by approvers in Settings. Includes removed ones
// (active = 0) so older defects still show their label.
const ClassificationsContext = createContext([]);

const classificationLabel = (classifications, key) => {
  if (!key) return 'N/A';
  const match = classifications.find(c => c.key === key);
  if (match) return match.name;
  return key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

// ============================================================================
// DEFECT MANAGEMENT SYSTEM
// A complete workflow for reporting, approving, and managing defects
// ============================================================================

// Microsoft Authentication Configuration
const msalConfig = {
  clientId: "443e37e9-c2c7-4bc2-857e-5e0be5da9e2b",
  tenantId: "ae8788cd-200c-43fd-9a67-df649b533f21",
  redirectUri: window.location.origin,
  scopes: ["openid", "profile", "email", "User.Read"]
};

// Exchange the Microsoft token for a DefectTrack session cookie. The API
// checks the token with Microsoft and rejects every request without a session.
const createApiSession = async (accessToken) => {
  const response = await fetch('/api/session', {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || 'Failed to start your DefectTrack session');
  }
};

// Generate random string for PKCE
const generateRandomString = (length) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
  let result = '';
  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);
  for (let i = 0; i < length; i++) {
    result += chars[randomValues[i] % chars.length];
  }
  return result;
};

// Generate code challenge for PKCE
const generateCodeChallenge = async (codeVerifier) => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

// Compress and resize image to fit within D1 storage limits
const compressImage = (file, maxWidth = 1200, quality = 0.7) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const compressedUrl = canvas.toDataURL('image/jpeg', quality);
        resolve({
          id: Date.now() + Math.random(),
          name: file.name,
          size: compressedUrl.length,
          type: 'image/jpeg',
          url: compressedUrl
        });
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
};

// ============================================================================
// STYLES
// ============================================================================

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Source+Sans+3:wght@300;400;500;600;700&display=swap');

  :root {
    --bg-primary: #0a0a0b;
    --bg-secondary: #111113;
    --bg-tertiary: #1a1a1d;
    --bg-elevated: #222225;
    --border-color: #2a2a2d;
    --border-focus: #3d3d42;
    --text-primary: #f5f5f7;
    --text-secondary: #a1a1a6;
    --text-muted: #6e6e73;
    --accent-blue: #0a84ff;
    --accent-green: #30d158;
    --accent-orange: #ff9f0a;
    --accent-red: #ff453a;
    --accent-purple: #bf5af2;
    --shadow-sm: 0 1px 2px rgba(0,0,0,0.4);
    --shadow-md: 0 4px 12px rgba(0,0,0,0.5);
    --shadow-lg: 0 8px 32px rgba(0,0,0,0.6);
    --radius-sm: 6px;
    --radius-md: 10px;
    --radius-lg: 16px;
    --font-mono: 'JetBrains Mono', monospace;
    --font-sans: 'Source Sans 3', -apple-system, sans-serif;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: var(--font-sans);
    background: var(--bg-primary);
    color: var(--text-primary);
    line-height: 1.6;
    min-height: 100vh;
  }

  /* Login Screen */
  .login-container {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: 
      radial-gradient(ellipse at 20% 20%, rgba(10, 132, 255, 0.08) 0%, transparent 50%),
      radial-gradient(ellipse at 80% 80%, rgba(191, 90, 242, 0.06) 0%, transparent 50%),
      var(--bg-primary);
    padding: 20px;
  }

  .login-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    padding: 48px;
    width: 100%;
    max-width: 420px;
    box-shadow: var(--shadow-lg);
  }

  .login-logo {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 32px;
  }

  .login-logo-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .login-logo-text {
    font-family: var(--font-mono);
    font-size: 20px;
    font-weight: 600;
    letter-spacing: -0.5px;
  }

  .login-title {
    font-size: 28px;
    font-weight: 600;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }

  .login-subtitle {
    color: var(--text-secondary);
    margin-bottom: 32px;
    font-size: 15px;
  }

  .ms-login-btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 14px 24px;
    background: #2f2f2f;
    border: 1px solid #3d3d3d;
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .ms-login-btn:hover {
    background: #3a3a3a;
    border-color: #4a4a4a;
    transform: translateY(-1px);
  }

  .ms-logo {
    width: 20px;
    height: 20px;
  }

  .login-divider {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0;
    color: var(--text-muted);
    font-size: 13px;
  }

  .login-divider::before,
  .login-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-color);
  }

  .demo-login-btn {
    width: 100%;
    padding: 12px 24px;
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .demo-login-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-focus);
    color: var(--text-primary);
  }

  /* Main Layout */
  .app-layout {
    display: flex;
    min-height: 100vh;
  }

  .sidebar {
    width: 260px;
    background: var(--bg-secondary);
    border-right: 1px solid var(--border-color);
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    position: fixed;
    height: 100vh;
    overflow-y: auto;
  }

  .sidebar-header {
    padding: 0 20px 20px;
    border-bottom: 1px solid var(--border-color);
    margin-bottom: 20px;
  }

  .sidebar-logo {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .sidebar-logo-icon {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .sidebar-logo-text {
    font-family: var(--font-mono);
    font-size: 15px;
    font-weight: 600;
  }

  .nav-section {
    padding: 0 12px;
    margin-bottom: 24px;
  }

  .nav-section-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    padding: 0 8px;
    margin-bottom: 8px;
  }

  .nav-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--radius-sm);
    color: var(--text-secondary);
    cursor: pointer;
    transition: all 0.15s ease;
    font-size: 14px;
  }

  .nav-item:hover {
    background: var(--bg-tertiary);
    color: var(--text-primary);
  }

  .nav-item.active {
    background: rgba(10, 132, 255, 0.15);
    color: var(--accent-blue);
  }

  .nav-item-icon {
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0.7;
  }

  .nav-item.active .nav-item-icon {
    opacity: 1;
  }

  .nav-badge {
    margin-left: auto;
    background: var(--accent-red);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
  }

  .sidebar-footer {
    margin-top: auto;
    padding: 20px;
    border-top: 1px solid var(--border-color);
  }

  .user-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .user-avatar {
    width: 36px;
    height: 36px;
    background: linear-gradient(135deg, var(--accent-orange), var(--accent-red));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 14px;
  }

  .user-details {
    flex: 1;
    min-width: 0;
  }

  .user-name {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .user-role {
    font-size: 12px;
    color: var(--text-muted);
  }

  .logout-btn {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    transition: color 0.15s;
  }

  .logout-btn:hover {
    color: var(--accent-red);
  }

  /* Main Content */
  .main-content {
    flex: 1;
    margin-left: 260px;
    padding: 32px;
    background: var(--bg-primary);
  }

  .page-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 32px;
  }

  .page-title {
    font-size: 28px;
    font-weight: 600;
    letter-spacing: -0.5px;
  }

  .page-subtitle {
    color: var(--text-secondary);
    font-size: 15px;
    margin-top: 4px;
  }

  .primary-btn {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 20px;
    background: var(--accent-blue);
    border: none;
    border-radius: var(--radius-md);
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .primary-btn:hover {
    background: #0977e5;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(10, 132, 255, 0.3);
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
    transition: all 0.2s ease;
  }

  .stat-card:hover {
    border-color: var(--border-focus);
    transform: translateY(-2px);
  }

  .stat-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .stat-label {
    font-size: 13px;
    color: var(--text-secondary);
    font-weight: 500;
  }

  .stat-icon {
    width: 32px;
    height: 32px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
  }

  .stat-icon.blue { background: rgba(10, 132, 255, 0.15); color: var(--accent-blue); }
  .stat-icon.orange { background: rgba(255, 159, 10, 0.15); color: var(--accent-orange); }
  .stat-icon.green { background: rgba(48, 209, 88, 0.15); color: var(--accent-green); }
  .stat-icon.purple { background: rgba(191, 90, 242, 0.15); color: var(--accent-purple); }
  .stat-icon.red { background: rgba(255, 69, 58, 0.15); color: var(--accent-red); }

  .stat-value {
    font-size: 32px;
    font-weight: 700;
    font-family: var(--font-mono);
    letter-spacing: -1px;
  }

  .stat-change {
    font-size: 12px;
    margin-top: 4px;
  }

  .stat-change.positive { color: var(--accent-green); }
  .stat-change.negative { color: var(--accent-red); }

  /* Filters */
  .filters-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .search-input {
    flex: 1;
    min-width: 200px;
    padding: 10px 16px;
    padding-left: 40px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 14px;
    transition: all 0.15s ease;
  }

  .search-input:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(10, 132, 255, 0.1);
  }

  .search-wrapper {
    position: relative;
    flex: 1;
    min-width: 200px;
  }

  .search-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-muted);
    pointer-events: none;
  }

  .filter-select {
    padding: 10px 16px;
    padding-right: 36px;
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' fill='%236e6e73' viewBox='0 0 16 16'%3E%3Cpath d='M8 11L3 6h10l-5 5z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 12px center;
    transition: all 0.15s ease;
  }

  .filter-select:focus {
    outline: none;
    border-color: var(--accent-blue);
  }

  /* Defects Table */
  .defects-table {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    overflow: hidden;
  }

  .table-header {
    display: grid;
    grid-template-columns: 100px 2fr 1fr 100px 140px 100px;
    gap: 16px;
    padding: 14px 20px;
    background: var(--bg-tertiary);
    border-bottom: 1px solid var(--border-color);
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  .table-row {
    display: grid;
    grid-template-columns: 100px 2fr 1fr 100px 140px 100px;
    gap: 16px;
    padding: 16px 20px;
    border-bottom: 1px solid var(--border-color);
    align-items: center;
    cursor: pointer;
    transition: background 0.15s ease;
  }

  .table-row:last-child {
    border-bottom: none;
  }

  .table-row:hover {
    background: var(--bg-tertiary);
  }

  .defect-id {
    font-family: var(--font-mono);
    font-size: 13px;
    font-weight: 500;
    color: var(--accent-blue);
  }

  .defect-title {
    font-weight: 500;
    font-size: 14px;
    margin-bottom: 2px;
  }

  .defect-store {
    font-size: 13px;
    color: var(--text-muted);
  }

  .severity-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    width: fit-content;
  }

  .severity-badge.urgent {
    background: rgba(255, 69, 58, 0.25);
    color: var(--accent-red);
    animation: urgentPulse 2s ease-in-out infinite;
  }

  @keyframes urgentPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.7; }
  }

  .severity-badge.high {
    background: rgba(255, 159, 10, 0.15);
    color: var(--accent-orange);
  }

  .severity-badge.medium {
    background: rgba(10, 132, 255, 0.15);
    color: var(--accent-blue);
  }

  .severity-badge.low {
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .severity-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  .status-badge {
    padding: 6px 12px;
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-weight: 500;
    text-align: center;
  }

  .status-badge.pending_approval {
    background: rgba(255, 159, 10, 0.15);
    color: var(--accent-orange);
  }

  .status-badge.contractor_assigned {
    background: rgba(10, 132, 255, 0.15);
    color: var(--accent-blue);
  }

  .status-badge.quote_received {
    background: rgba(191, 90, 242, 0.15);
    color: var(--accent-purple);
  }

  .status-badge.quote_approved {
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .status-badge.completed {
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .status-badge.rejected {
    background: rgba(255, 69, 58, 0.15);
    color: var(--accent-red);
  }

  .status-badge.revision_requested {
    background: rgba(255, 159, 10, 0.15);
    color: var(--accent-orange);
  }

  .table-date {
    font-size: 13px;
    color: var(--text-secondary);
  }

  /* Modal */
  .modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.8);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    animation: fadeIn 0.2s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  .modal {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-lg);
    width: 100%;
    max-width: 640px;
    max-height: 90vh;
    overflow-y: auto;
    animation: slideUp 0.3s ease;
  }

  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px);
    }
    to { 
      opacity: 1;
      transform: translateY(0);
    }
  }

  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 20px 24px;
    border-bottom: 1px solid var(--border-color);
  }

  .modal-title {
    font-size: 18px;
    font-weight: 600;
  }

  .modal-close {
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    transition: color 0.15s;
  }

  .modal-close:hover {
    color: var(--text-primary);
  }

  .modal-body {
    padding: 24px;
  }

  .form-group {
    margin-bottom: 20px;
  }

  .form-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: var(--text-secondary);
    margin-bottom: 8px;
  }

  .form-input,
  .form-textarea,
  .form-select {
    width: 100%;
    padding: 12px 16px;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-primary);
    font-size: 14px;
    font-family: inherit;
    transition: all 0.15s ease;
  }

  .form-input:focus,
  .form-textarea:focus,
  .form-select:focus {
    outline: none;
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 3px rgba(10, 132, 255, 0.1);
  }

  .form-textarea {
    min-height: 120px;
    resize: vertical;
  }

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }

  /* Priority Options */
  .priority-options {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .priority-option {
    display: flex;
    flex-direction: column;
    padding: 12px 16px;
    background: var(--bg-tertiary);
    border: 2px solid var(--border-color);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .priority-option:hover {
    border-color: var(--border-focus);
  }

  .priority-option.selected {
    border-color: var(--accent-blue);
    background: rgba(10, 132, 255, 0.1);
  }

  .priority-option.selected.priority-urgent {
    border-color: var(--accent-red);
    background: rgba(255, 69, 58, 0.1);
  }

  .priority-option.selected.priority-high {
    border-color: var(--accent-orange);
    background: rgba(255, 159, 10, 0.1);
  }

  .priority-option.selected.priority-medium {
    border-color: var(--accent-blue);
    background: rgba(10, 132, 255, 0.1);
  }

  .priority-option.selected.priority-low {
    border-color: var(--accent-green);
    background: rgba(48, 209, 88, 0.1);
  }

  .priority-label {
    font-size: 14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .priority-description {
    font-size: 12px;
    color: var(--text-muted);
    margin-top: 2px;
  }

  .modal-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 20px 24px;
    border-top: 1px solid var(--border-color);
  }

  .secondary-btn {
    padding: 10px 20px;
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .secondary-btn:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-focus);
    color: var(--text-primary);
  }

  /* Defect Detail Modal */
  .detail-section {
    margin-bottom: 24px;
  }

  .detail-section-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin-bottom: 12px;
  }

  .detail-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  .detail-item {
    background: var(--bg-tertiary);
    padding: 12px 16px;
    border-radius: var(--radius-sm);
  }

  .detail-item-label {
    font-size: 12px;
    color: var(--text-muted);
    margin-bottom: 4px;
  }

  .detail-item-value {
    font-size: 14px;
    font-weight: 500;
  }

  .detail-description {
    background: var(--bg-tertiary);
    padding: 16px;
    border-radius: var(--radius-sm);
    font-size: 14px;
    line-height: 1.6;
    color: var(--text-secondary);
  }

  .workflow-timeline {
    position: relative;
    padding-left: 24px;
  }

  .workflow-timeline::before {
    content: '';
    position: absolute;
    left: 7px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: var(--border-color);
  }

  .timeline-item {
    position: relative;
    padding-bottom: 20px;
  }

  .timeline-item:last-child {
    padding-bottom: 0;
  }

  .timeline-dot {
    position: absolute;
    left: -24px;
    top: 2px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: var(--bg-secondary);
    border: 2px solid var(--border-color);
  }

  .timeline-dot.completed {
    background: var(--accent-green);
    border-color: var(--accent-green);
  }

  .timeline-dot.rejected {
    background: var(--accent-red);
    border-color: var(--accent-red);
  }

  .timeline-dot.active {
    background: var(--accent-blue);
    border-color: var(--accent-blue);
    box-shadow: 0 0 0 4px rgba(10, 132, 255, 0.2);
  }

  .timeline-content {
    background: var(--bg-tertiary);
    padding: 12px 16px;
    border-radius: var(--radius-sm);
  }

  .timeline-title {
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 4px;
  }

  .timeline-meta {
    font-size: 12px;
    color: var(--text-muted);
  }

  .action-buttons {
    display: flex;
    gap: 12px;
    margin-top: 20px;
  }

  .approve-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    background: var(--accent-green);
    border: none;
    border-radius: var(--radius-md);
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .approve-btn:hover {
    background: #28b84d;
    transform: translateY(-1px);
  }

  .approve-btn svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  .reject-btn {
    flex: 1;
    padding: 12px 20px;
    background: transparent;
    border: 1px solid var(--accent-red);
    border-radius: var(--radius-md);
    color: var(--accent-red);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .reject-btn:hover {
    background: rgba(255, 69, 58, 0.1);
  }

  .revision-btn {
    flex: 1;
    padding: 12px 20px;
    background: transparent;
    border: 1px solid var(--accent-orange);
    border-radius: var(--radius-md);
    color: var(--accent-orange);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .revision-btn:hover {
    background: rgba(255, 159, 10, 0.1);
  }

  .approve-bypass-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 20px;
    background: transparent;
    border: 1px solid var(--accent-purple);
    border-radius: var(--radius-md);
    color: var(--accent-purple);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .approve-bypass-btn:hover {
    background: rgba(191, 90, 242, 0.1);
  }

  .approve-bypass-btn svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  /* Quote Section */
  .quote-card {
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
  }

  .quote-amount {
    font-size: 28px;
    font-weight: 700;
    font-family: var(--font-mono);
    color: var(--accent-green);
    margin-bottom: 8px;
  }

  .quote-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 12px;
  }

  .quote-meta {
    font-size: 12px;
    color: var(--text-muted);
  }

  /* Toast Notification */
  .toast {
    position: fixed;
    bottom: 24px;
    right: 24px;
    background: var(--bg-elevated);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 16px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: var(--shadow-lg);
    animation: slideIn 0.3s ease;
    z-index: 1100;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  .toast-icon {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
  }

  .toast-icon.success {
    background: rgba(48, 209, 88, 0.15);
    color: var(--accent-green);
  }

  .toast-icon.error {
    background: rgba(255, 69, 58, 0.15);
    color: var(--accent-red);
  }

  .toast-message {
    font-size: 14px;
  }

  /* Contractors Page */
  .contractors-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
    gap: 20px;
  }

  .contractor-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
    transition: all 0.2s ease;
  }

  .contractor-card:hover {
    border-color: var(--border-focus);
    transform: translateY(-2px);
  }

  .contractor-name {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
  }

  .contractor-specialty {
    font-size: 13px;
    color: var(--accent-blue);
    margin-bottom: 12px;
  }

  .contractor-email {
    font-size: 14px;
    color: var(--text-secondary);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Empty State */
  .empty-state {
    text-align: center;
    padding: 60px 20px;
    color: var(--text-secondary);
  }

  .empty-state-icon {
    font-size: 48px;
    margin-bottom: 16px;
    opacity: 0.5;
  }

  .empty-state-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary);
    margin-bottom: 8px;
  }

  .empty-state-text {
    font-size: 14px;
  }

  /* Image Upload Zone */
  .image-upload-zone {
    border: 2px dashed var(--border-color);
    border-radius: var(--radius-md);
    padding: 32px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.2s ease;
    background: var(--bg-tertiary);
  }

  .image-upload-zone:hover {
    border-color: var(--accent-blue);
    background: rgba(10, 132, 255, 0.05);
  }

  .image-upload-zone.drag-active {
    border-color: var(--accent-blue);
    background: rgba(10, 132, 255, 0.1);
    border-style: solid;
  }

  .upload-icon {
    color: var(--text-muted);
    margin-bottom: 12px;
    display: flex;
    justify-content: center;
  }

  .upload-text {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 4px;
  }

  .upload-text-primary {
    color: var(--accent-blue);
    font-weight: 500;
  }

  .upload-hint {
    font-size: 12px;
    color: var(--text-muted);
  }

  /* Image Preview Grid */
  .image-preview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
    gap: 12px;
    margin-top: 16px;
  }

  .image-preview-item {
    position: relative;
    border-radius: var(--radius-sm);
    overflow: hidden;
    background: var(--bg-tertiary);
    border: 1px solid var(--border-color);
  }

  .image-preview-img {
    width: 100%;
    height: 100px;
    object-fit: cover;
    display: block;
  }

  .image-preview-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .image-preview-item:hover .image-preview-overlay {
    opacity: 1;
  }

  .image-remove-btn {
    background: var(--accent-red);
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    cursor: pointer;
    transition: transform 0.15s ease;
  }

  .image-remove-btn:hover {
    transform: scale(1.1);
  }

  .image-preview-info {
    padding: 8px;
  }

  .image-preview-name {
    display: block;
    font-size: 11px;
    color: var(--text-primary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .image-preview-size {
    font-size: 10px;
    color: var(--text-muted);
  }

  /* Image Gallery in Detail Modal */
  .detail-images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
    gap: 12px;
  }

  .detail-image-item {
    border-radius: var(--radius-sm);
    overflow: hidden;
    border: 1px solid var(--border-color);
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .detail-image-item:hover {
    border-color: var(--accent-blue);
    transform: scale(1.02);
  }

  .detail-image-item img {
    width: 100%;
    height: 120px;
    object-fit: cover;
    display: block;
  }

  /* Mobile Header */
  .mobile-header {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    background: var(--bg-secondary);
    border-bottom: 1px solid var(--border-color);
    align-items: center;
    padding: 0 16px;
    z-index: 1000;
    gap: 12px;
  }

  .mobile-menu-btn {
    background: none;
    border: none;
    color: var(--text-primary);
    cursor: pointer;
    padding: 8px;
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
  }

  .mobile-menu-btn:active {
    background: var(--bg-tertiary);
  }

  .mobile-header-logo {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  /* Sidebar Overlay */
  .sidebar-overlay {
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    z-index: 1000;
    animation: fadeIn 0.2s ease;
  }

  /* Responsive - Tablet */
  @media (max-width: 1024px) {
    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .table-header,
    .table-row {
      grid-template-columns: 80px 1.5fr 1fr 80px 120px;
    }

    .table-header > *:last-child,
    .table-row > *:last-child {
      display: none;
    }
  }

  /* Responsive - Mobile */
  @media (max-width: 768px) {
    .mobile-header {
      display: flex;
    }

    .sidebar-overlay {
      display: block;
    }

    .sidebar {
      position: fixed;
      z-index: 1001;
      top: 0;
      left: 0;
      height: 100vh;
      transform: translateX(-100%);
      transition: transform 0.3s ease;
      box-shadow: var(--shadow-lg);
    }

    .sidebar.open {
      transform: translateX(0);
    }

    .main-content {
      margin-left: 0;
      padding: 16px;
      padding-top: 72px;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 12px;
    }

    .stat-card {
      padding: 16px;
    }

    .page-header {
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
      margin-bottom: 20px;
    }

    .page-title {
      font-size: 22px;
    }

    /* Filters - stack vertically */
    .filters-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .search-wrapper {
      min-width: unset;
      width: 100%;
    }

    .search-input {
      min-width: unset;
      width: 100%;
    }

    .filter-select {
      width: 100%;
    }

    /* Table - card layout */
    .table-header {
      display: none !important;
    }

    .table-row {
      display: flex !important;
      flex-wrap: wrap;
      gap: 8px;
      padding: 14px 16px;
      align-items: center;
    }

    .table-row .defect-id {
      font-size: 12px;
    }

    .table-row > div:nth-child(2) {
      flex: 1;
      min-width: 0;
    }

    .table-row .defect-title {
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .table-row > div:nth-child(3) {
      display: none;
    }

    .table-row > div:nth-child(6) {
      display: none;
    }

    .table-row > div:nth-child(4),
    .table-row > div:nth-child(5) {
      flex-shrink: 0;
    }

    .severity-badge {
      padding: 3px 8px;
      font-size: 11px;
    }

    .status-badge {
      padding: 4px 8px;
      font-size: 11px;
    }

    /* Modal - near full screen */
    .modal-overlay {
      padding: 0;
      align-items: flex-end;
    }

    .modal {
      max-width: 100%;
      max-height: 95vh;
      border-radius: var(--radius-lg) var(--radius-lg) 0 0;
      animation: slideUpMobile 0.3s ease;
    }

    @keyframes slideUpMobile {
      from { transform: translateY(100%); }
      to { transform: translateY(0); }
    }

    .modal-body {
      padding: 16px;
    }

    .modal-header {
      padding: 16px;
    }

    .modal-footer {
      padding: 16px;
    }

    /* Detail modal grid */
    .detail-grid {
      grid-template-columns: 1fr;
    }

    /* Forms */
    .form-row {
      grid-template-columns: 1fr;
    }

    /* Buttons - larger touch targets */
    .primary-btn,
    .approve-btn,
    .reject-btn,
    .revision-btn,
    .approve-bypass-btn {
      min-height: 44px;
      font-size: 14px;
    }

    .action-buttons {
      flex-direction: column;
    }

    .action-buttons button {
      width: 100%;
    }

    /* Login card */
    .login-card {
      padding: 32px 20px;
    }
  }

  /* Responsive - Small phones */
  @media (max-width: 480px) {
    .main-content {
      padding: 12px;
      padding-top: 68px;
    }

    .stats-grid {
      grid-template-columns: 1fr;
      gap: 8px;
    }

    .page-title {
      font-size: 20px;
    }

    .login-card {
      padding: 24px 16px;
    }

    .login-title {
      font-size: 22px;
    }
  }

  /* Contractor Portal Styles */
  .contractor-tabs {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    border-bottom: 1px solid var(--border-color);
    padding-bottom: 12px;
    flex-wrap: wrap;
  }

  .contractor-tab {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 16px;
    background: transparent;
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    color: var(--text-secondary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .contractor-tab:hover {
    background: var(--bg-tertiary);
    border-color: var(--border-focus);
    color: var(--text-primary);
  }

  .contractor-tab.active {
    background: rgba(10, 132, 255, 0.15);
    border-color: var(--accent-blue);
    color: var(--accent-blue);
  }

  .tab-badge {
    background: var(--accent-red);
    color: white;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 10px;
  }

  .tab-badge.yellow {
    background: var(--accent-orange);
  }

  .tab-badge.green {
    background: var(--accent-green);
  }

  .contractor-defects-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 20px;
  }

  .contractor-defect-card {
    background: var(--bg-secondary);
    border: 1px solid var(--border-color);
    border-radius: var(--radius-md);
    padding: 20px;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .contractor-defect-card:hover {
    border-color: var(--accent-blue);
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }

  .contractor-defect-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .contractor-defect-title {
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 4px;
    line-height: 1.4;
  }

  .contractor-defect-store {
    font-size: 13px;
    color: var(--accent-blue);
    margin-bottom: 8px;
  }

  .contractor-defect-description {
    font-size: 14px;
    color: var(--text-secondary);
    margin-bottom: 16px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .contractor-defect-quote {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: var(--bg-tertiary);
    border-radius: var(--radius-sm);
    margin-bottom: 16px;
  }

  .quote-label {
    font-size: 12px;
    color: var(--text-muted);
  }

  .quote-value {
    font-family: var(--font-mono);
    font-size: 16px;
    font-weight: 600;
    color: var(--accent-green);
  }

  .contractor-defect-footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid var(--border-color);
  }

  .contractor-defect-date {
    font-size: 12px;
    color: var(--text-muted);
  }

  .view-details-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 12px;
    background: transparent;
    border: 1px solid var(--accent-blue);
    border-radius: var(--radius-sm);
    color: var(--accent-blue);
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .view-details-btn:hover {
    background: var(--accent-blue);
    color: white;
  }

  .quote-status-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 12px;
    background: rgba(255, 159, 10, 0.1);
    border: 1px solid rgba(255, 159, 10, 0.3);
    border-radius: var(--radius-sm);
    color: var(--accent-orange);
    font-size: 14px;
    font-weight: 500;
  }

  .quote-status-banner.approved {
    background: rgba(48, 209, 88, 0.1);
    border-color: rgba(48, 209, 88, 0.3);
    color: var(--accent-green);
  }

  .status-icon {
    font-size: 16px;
  }
`;

// ============================================================================
// ICONS
// ============================================================================

const Icons = {
  Logo: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
    </svg>
  ),
  Dashboard: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="7" height="9" rx="1"/>
      <rect x="14" y="3" width="7" height="5" rx="1"/>
      <rect x="14" y="12" width="7" height="9" rx="1"/>
      <rect x="3" y="16" width="7" height="5" rx="1"/>
    </svg>
  ),
  Defects: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  ),
  Approvals: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 11l3 3L22 4"/>
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
    </svg>
  ),
  Contractors: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/>
      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Wrench: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
    </svg>
  ),
  Reports: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/>
      <polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  Settings: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <line x1="12" y1="5" x2="12" y2="19"/>
      <line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  ),
  Search: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"/>
      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
    </svg>
  ),
  Close: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="18" y1="6" x2="6" y2="18"/>
      <line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  ),
  Logout: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/>
      <line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  Check: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  ),
  CheckCircle: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  ),
  InProgress: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <polyline points="12 6 12 12 16 14"/>
    </svg>
  ),
  Mail: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  ),
  Microsoft: () => (
    <svg width="20" height="20" viewBox="0 0 23 23">
      <rect x="1" y="1" width="10" height="10" fill="#f25022"/>
      <rect x="12" y="1" width="10" height="10" fill="#7fba00"/>
      <rect x="1" y="12" width="10" height="10" fill="#00a4ef"/>
      <rect x="12" y="12" width="10" height="10" fill="#ffb900"/>
    </svg>
  ),
  Camera: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  Image: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
      <circle cx="8.5" cy="8.5" r="1.5"/>
      <polyline points="21 15 16 10 5 21"/>
    </svg>
  ),
  Download: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/>
      <line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  ),
  Menu: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="3" y1="6" x2="21" y2="6"/>
      <line x1="3" y1="12" x2="21" y2="12"/>
      <line x1="3" y1="18" x2="21" y2="18"/>
    </svg>
  ),
  Edit: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="22" y1="2" x2="11" y2="13"/>
      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
    </svg>
  ),
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 4 23 10 17 10"/>
      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
    </svg>
  )
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Login Screen
const LoginScreen = ({ onLogin, isLoading, error }) => {
  const handleMicrosoftLogin = async () => {
    try {
      // Generate PKCE values
      const codeVerifier = generateRandomString(64);
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const state = generateRandomString(32);
      
      // Store verifier and state for later
      sessionStorage.setItem('pkce_verifier', codeVerifier);
      sessionStorage.setItem('auth_state', state);
      
      // Build authorization URL
      const authUrl = new URL(`https://login.microsoftonline.com/${msalConfig.tenantId}/oauth2/v2.0/authorize`);
      authUrl.searchParams.append('client_id', msalConfig.clientId);
      authUrl.searchParams.append('response_type', 'code');
      authUrl.searchParams.append('redirect_uri', msalConfig.redirectUri);
      authUrl.searchParams.append('scope', msalConfig.scopes.join(' '));
      authUrl.searchParams.append('response_mode', 'query');
      authUrl.searchParams.append('state', state);
      authUrl.searchParams.append('code_challenge', codeChallenge);
      authUrl.searchParams.append('code_challenge_method', 'S256');
      
      // Redirect to Microsoft login
      window.location.href = authUrl.toString();
    } catch (err) {
      console.error('Login error:', err);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo">
          <div className="login-logo-icon">
            <Icons.Logo />
          </div>
          <span className="login-logo-text">DefectTrack</span>
        </div>
        <h1 className="login-title">Welcome back</h1>
        <p className="login-subtitle">Sign in to manage and track defects across your organization.</p>
        
        {error && (
          <div style={{ 
            background: 'rgba(255, 69, 58, 0.1)', 
            border: '1px solid var(--accent-red)', 
            borderRadius: 'var(--radius-md)', 
            padding: '12px 16px',
            marginBottom: '16px',
            fontSize: '14px',
            color: 'var(--accent-red)'
          }}>
            {error}
          </div>
        )}
        
        <button 
          className="ms-login-btn" 
          onClick={handleMicrosoftLogin}
          disabled={isLoading}
        >
          {isLoading ? (
            <span>Signing in...</span>
          ) : (
            <>
              <Icons.Microsoft />
              Sign in with Microsoft 365
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// Sidebar Navigation
const Sidebar = ({ currentPage, setCurrentPage, user, onLogout, pendingCount, isApprover, isOpen, onClose }) => {
  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <Icons.Dashboard /> },
    { id: 'defects', label: 'All Defects', icon: <Icons.Defects /> },
    { id: 'approvals', label: 'Approvals', icon: <Icons.Approvals />, badge: pendingCount, approverOnly: true },
    { id: 'contractors', label: 'Contractors', icon: <Icons.Contractors />, approverOnly: true },
    { id: 'reports', label: 'Reports', icon: <Icons.Reports />, approverOnly: true },
  ];

  const visibleNavItems = navItems.filter(item => !item.approverOnly || isApprover);

  const handleNavClick = (pageId) => {
    setCurrentPage(pageId);
    if (onClose) onClose();
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <div className="sidebar-logo-icon">
              <Icons.Logo />
            </div>
            <span className="sidebar-logo-text">DefectTrack</span>
          </div>
        </div>

        <nav className="nav-section">
          <div className="nav-section-title">Main Menu</div>
          {visibleNavItems.map(item => (
            <div
              key={item.id}
              className={`nav-item ${currentPage === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id)}
            >
              <span className="nav-item-icon">{item.icon}</span>
              {item.label}
              {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
            </div>
          ))}
        </nav>

        <nav className="nav-section">
          <div className="nav-section-title">System</div>
          {isApprover && (
            <div
              className={`nav-item ${currentPage === 'settings' ? 'active' : ''}`}
              onClick={() => handleNavClick('settings')}
            >
              <span className="nav-item-icon"><Icons.Settings /></span>
              Settings
            </div>
          )}
        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="user-details">
              <div className="user-name">{user.name}</div>
              <div className="user-role">{isApprover ? 'Approver' : 'Staff'}</div>
            </div>
            <button className="logout-btn" onClick={onLogout} title="Sign out">
              <Icons.Logout />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Stats Card Component
const StatsCard = ({ label, value, icon, color, change }) => (
  <div className="stat-card">
    <div className="stat-header">
      <span className="stat-label">{label}</span>
      <div className={`stat-icon ${color}`}>{icon}</div>
    </div>
    <div className="stat-value">{value}</div>
    {change && (
      <div className={`stat-change ${change > 0 ? 'positive' : 'negative'}`}>
        {change > 0 ? '↑' : '↓'} {Math.abs(change)}% from last month
      </div>
    )}
  </div>
);

// Dashboard Page
const DashboardPage = ({ defects, setCurrentPage, onSelectDefect, onAddDefect, stores }) => {
  const [showNewDefect, setShowNewDefect] = useState(false);
  const stats = {
    total: defects.length,
    pending: defects.filter(d => d.status === 'pending_approval').length,
    inProgress: defects.filter(d => ['contractor_assigned', 'quote_received', 'quote_approved', 'revision_requested'].includes(d.status)).length,
    completed: defects.filter(d => d.status === 'completed').length,
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Defect management activities</p>
        </div>
        <button className="primary-btn" onClick={() => setShowNewDefect(true)}>
          <Icons.Plus /> Report Defect
        </button>
      </div>

      <div className="stats-grid">
        <StatsCard
          label="Total Defects"
          value={stats.total}
          icon={<Icons.Defects />}
          color="blue"
        />
        <StatsCard
          label="Pending Approval"
          value={stats.pending}
          icon={<Icons.Approvals />}
          color="orange"
        />
        <StatsCard
          label="In Progress"
          value={stats.inProgress}
          icon={<Icons.InProgress />}
          color="purple"
        />
        <StatsCard
          label="Completed"
          value={stats.completed}
          icon={<Icons.CheckCircle />}
          color="green"
        />
      </div>

      <div className="page-header" style={{ marginTop: 40 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 600 }}>Recent Defects</h2>
        </div>
        <button className="secondary-btn" onClick={() => setCurrentPage('defects')}>
          View All
        </button>
      </div>

      <DefectsTable defects={defects.slice(0, 5)} onSelectDefect={onSelectDefect} />

      {showNewDefect && (
        <NewDefectModal
          onClose={() => setShowNewDefect(false)}
          onSubmit={(data) => {
            onAddDefect(data);
            setShowNewDefect(false);
          }}
          stores={stores}
        />
      )}
    </div>
  );
};

// Defects Table
const DefectsTable = ({ defects, onSelectDefect }) => {
  const formatStatus = (status) => {
    const labels = {
      pending_approval: 'Pending',
      contractor_assigned: 'Assigned',
      quote_received: 'Quote Ready',
      quote_approved: 'Work Approved',
      revision_requested: 'Revision',
      rejected: 'Rejected',
      completed: 'Completed'
    };
    return labels[status] || status;
  };

  const formatPriority = (severity) => {
    const labels = {
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low'
    };
    return labels[severity] || severity.charAt(0).toUpperCase() + severity.slice(1);
  };

  return (
    <div className="defects-table">
      <div className="table-header">
        <div>ID</div>
        <div>Defect</div>
        <div>Reported By</div>
        <div>Priority</div>
        <div>Status</div>
        <div>Date</div>
      </div>
      {defects.map(defect => (
        <div 
          key={defect.id} 
          className="table-row"
          onClick={() => onSelectDefect && onSelectDefect(defect)}
        >
          <div className="defect-id">{defect.id}</div>
          <div>
            <div className="defect-title">{defect.title}</div>
            <div className="defect-store">{defect.location}</div>
          </div>
          <div style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{defect.reportedBy}</div>
          <div>
            <span className={`severity-badge ${defect.severity}`}>
              <span className="severity-dot"></span>
              {formatPriority(defect.severity)}
            </span>
          </div>
          <div>
            <span className={`status-badge ${defect.status}`}>
              {formatStatus(defect.status)}
            </span>
          </div>
          <div className="table-date">{defect.reportedDate}</div>
        </div>
      ))}
    </div>
  );
};

// Defects List Page
const DefectsPage = ({ defects, onAddDefect, onSelectDefect, stores }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);

  const filteredDefects = defects.filter(defect => {
    const matchesSearch = defect.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         defect.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         defect.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || defect.status === statusFilter;
    const matchesSeverity = severityFilter === 'all' || defect.severity === severityFilter;
    return matchesSearch && matchesStatus && matchesSeverity;
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Defects</h1>
          <p className="page-subtitle">Manage and track all reported defects</p>
        </div>
        <button className="primary-btn" onClick={() => setShowModal(true)}>
          <Icons.Plus />
          Report Defect
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-wrapper">
          <span className="search-icon"><Icons.Search /></span>
          <input
            type="text"
            className="search-input"
            placeholder="Search defects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="pending_approval">Pending Approval</option>
          <option value="contractor_assigned">Contractor Assigned</option>
          <option value="quote_received">Quote Received</option>
          <option value="quote_approved">Work Approved</option>
          <option value="completed">Completed</option>
        </select>
        <select 
          className="filter-select"
          value={severityFilter}
          onChange={(e) => setSeverityFilter(e.target.value)}
        >
          <option value="all">All Priorities</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {filteredDefects.length > 0 ? (
        <DefectsTable defects={filteredDefects} onSelectDefect={onSelectDefect} />
      ) : (
        <div className="defects-table">
          <div className="empty-state">
            <div className="empty-state-icon">📋</div>
            <div className="empty-state-title">No defects found</div>
            <div className="empty-state-text">Try adjusting your search or filters</div>
          </div>
        </div>
      )}

      {showModal && (
        <NewDefectModal
          onClose={() => setShowModal(false)}
          onSubmit={(data) => {
            onAddDefect(data);
            setShowModal(false);
          }}
          stores={stores}
        />
      )}
    </div>
  );
};

// New Defect Modal
const NewDefectModal = ({ onClose, onSubmit, stores }) => {
  const classifications = useContext(ClassificationsContext);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    store: '',
    severity: 'medium',
    classification: '',
    reporterName: '',
    images: []
  });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent', description: 'Danger' },
    { value: 'high', label: 'High', description: 'Prevents staff from working effectively' },
    { value: 'medium', label: 'Medium', description: 'Significant impact on customer impression' },
    { value: 'low', label: 'Low', description: 'Minor issue but needs to be resolved' }
  ];

  const classificationOptions = classifications
    .filter(c => c.active === 1)
    .map(c => ({ value: c.key, label: c.name }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    for (const file of validFiles) {
      const compressed = await compressImage(file);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, compressed]
      }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeImage = (imageId) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img.id !== imageId)
    }));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Report New Defect</h2>
          <button className="modal-close" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Defect Title *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Brief description of the defect"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea
                className="form-textarea"
                placeholder="Provide detailed information about the defect..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Store *</label>
                <select
                  className="form-select"
                  value={formData.store}
                  onChange={(e) => setFormData({ ...formData, store: e.target.value })}
                  required
                >
                  <option value="">Select a store...</option>
                  {stores && stores.map(store => (
                    <option key={store.id} value={store.name}>{store.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Classification *</label>
                <select
                  className="form-select"
                  value={formData.classification}
                  onChange={(e) => setFormData({ ...formData, classification: e.target.value })}
                  required
                >
                  <option value="">Select classification...</option>
                  {classificationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Reported By *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Name of the person who reported this issue"
                value={formData.reporterName}
                onChange={(e) => setFormData({ ...formData, reporterName: e.target.value })}
                required
              />
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                Enter the name of the staff member who reported this defect to you
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Priority *</label>
              <div className="priority-options">
                {priorityOptions.map(opt => (
                  <label 
                    key={opt.value} 
                    className={`priority-option ${formData.severity === opt.value ? 'selected' : ''} priority-${opt.value}`}
                  >
                    <input
                      type="radio"
                      name="severity"
                      value={opt.value}
                      checked={formData.severity === opt.value}
                      onChange={(e) => setFormData({ ...formData, severity: e.target.value })}
                      style={{ display: 'none' }}
                    />
                    <div className="priority-label">{opt.label}</div>
                    <div className="priority-description">{opt.description}</div>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="form-group">
              <label className="form-label">Attach Images</label>
              <div 
                className={`image-upload-zone ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                />
                <div className="upload-icon">
                  <Icons.Camera />
                </div>
                <div className="upload-text">
                  <span className="upload-text-primary">Click to upload</span> or drag and drop
                </div>
                <div className="upload-hint">PNG, JPG, GIF up to 10MB</div>
              </div>
              
              {formData.images.length > 0 && (
                <div className="image-preview-grid">
                  {formData.images.map(image => (
                    <div key={image.id} className="image-preview-item">
                      <img src={image.url} alt={image.name} className="image-preview-img" />
                      <div className="image-preview-overlay">
                        <button 
                          type="button"
                          className="image-remove-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeImage(image.id);
                          }}
                        >
                          <Icons.Close />
                        </button>
                      </div>
                      <div className="image-preview-info">
                        <span className="image-preview-name">{image.name}</span>
                        <span className="image-preview-size">{formatFileSize(image.size)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="secondary-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn">
              Submit Defect
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Defect Detail Modal
const DefectDetailModal = ({ defect, onClose, onApprove, onReject, onRequestRevision, onAssignContractor, onReassignContractor, onSendReminder, onManualComplete, contractors, isApprover }) => {
  const classifications = useContext(ClassificationsContext);
  const [showContractorSelect, setShowContractorSelect] = useState(false);
  const [selectedContractor, setSelectedContractor] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRevisionModal, setShowRevisionModal] = useState(false);
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveContractor, setApproveContractor] = useState('');
  const [approveType, setApproveType] = useState('defect'); // 'defect' or 'bypass'
  const [rejectReason, setRejectReason] = useState('');
  const [revisionComments, setRevisionComments] = useState('');
  const [showReassignSelect, setShowReassignSelect] = useState(false);
  const [reassignContractor, setReassignContractor] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const modalRef = React.useRef(null);

  // Auto-scroll to bottom when reject or revision or approve modal opens
  useEffect(() => {
    if ((showRejectModal || showRevisionModal || showApproveModal) && modalRef.current) {
      setTimeout(() => {
        modalRef.current.scrollTo({
          top: modalRef.current.scrollHeight,
          behavior: 'smooth'
        });
      }, 50);
    }
  }, [showRejectModal, showRevisionModal, showApproveModal]);

  const getWorkflowStatus = () => {
    const isRejected = defect.status === 'rejected';
    const steps = [
      { label: 'Defect Reported', completed: true, date: defect.reportedDate },
      { 
        label: isRejected ? 'Defect Rejected' : 'Defect Approved', 
        completed: defect.approvals.some(a => a.type === 'defect') || isRejected,
        rejected: isRejected,
        date: isRejected ? defect.rejectedDate : defect.approvals.find(a => a.type === 'defect')?.date,
        approver: isRejected ? defect.rejectedBy : defect.approvals.find(a => a.type === 'defect')?.approvedBy,
        rejectionReason: isRejected ? defect.rejectionReason : null
      },
      { 
        label: 'Contractor Assigned', 
        completed: !!defect.contractor && !isRejected,
        contractor: defect.contractor?.name
      },
      { 
        label: 'Quote Received', 
        completed: !!defect.quote && !isRejected,
        amount: defect.quote?.amount
      },
      { 
        label: 'Quote Approved', 
        completed: defect.approvals.some(a => a.type === 'quote') && !isRejected,
        date: defect.approvals.find(a => a.type === 'quote')?.date
      },
      { label: 'Work Completed', completed: defect.status === 'completed' }
    ];
    return steps;
  };

  const handleAssignContractor = () => {
    if (selectedContractor) {
      const contractor = contractors.find(c => c.id === parseInt(selectedContractor));
      onAssignContractor(defect.id, contractor);
      setShowContractorSelect(false);
    }
  };

  const handleApproveWithContractor = () => {
    if (approveContractor) {
      const contractor = contractors.find(c => c.id === parseInt(approveContractor));
      // First approve the defect, then assign contractor
      onApprove(defect.id, approveType, contractor);
      setShowApproveModal(false);
      onClose();
    }
  };

  const openApproveModal = (type) => {
    setApproveType(type);
    setApproveContractor('');
    setShowApproveModal(true);
  };

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(defect.id, rejectReason);
      setShowRejectModal(false);
      onClose();
    }
  };

  const handleRequestRevision = () => {
    if (revisionComments.trim()) {
      onRequestRevision(defect.id, revisionComments);
      setShowRevisionModal(false);
      onClose();
    }
  };

  const workflowSteps = getWorkflowStatus();
  const activeStepIndex = workflowSteps.findIndex(s => !s.completed);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div ref={modalRef} className="modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="defect-id" style={{ marginRight: 12 }}>{defect.id}</span>
            <span className={`severity-badge ${defect.severity}`}>
              <span className="severity-dot"></span>
              {defect.severity.charAt(0).toUpperCase() + defect.severity.slice(1)}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>
        <div className="modal-body">
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{defect.title}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>Store: {defect.location}</p>
          
          <div className="detail-section">
            <div className="detail-section-title">Description</div>
            <div className="detail-description">{defect.description}</div>
          </div>

          {defect.images && defect.images.length > 0 && (
            <div className="detail-section">
              <div className="detail-section-title">Attached Images ({defect.images.length})</div>
              <div className="detail-images-grid">
                {defect.images.map((image, index) => (
                  <div key={image.id || index} className="detail-image-item">
                    <img src={image.url} alt={image.name || `Image ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-section-title">Details</div>
            <div className="detail-grid">
              {defect.reporterName && (
                <div className="detail-item">
                  <div className="detail-item-label">Reported By</div>
                  <div className="detail-item-value">{defect.reporterName}</div>
                </div>
              )}
              <div className="detail-item">
                <div className="detail-item-label">Reported By (Manager)</div>
                <div className="detail-item-value">{defect.reportedBy}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Report Date</div>
                <div className="detail-item-value">{defect.reportedDate}</div>
              </div>
              {defect.classification && (
                <div className="detail-item">
                  <div className="detail-item-label">Classification</div>
                  <div className="detail-item-value" style={{ textTransform: 'capitalize' }}>
                    {classificationLabel(classifications, defect.classification)}
                  </div>
                </div>
              )}
              {defect.contractor && (
                <>
                  <div className="detail-item">
                    <div className="detail-item-label">Assigned Contractor</div>
                    <div className="detail-item-value">{defect.contractor.name}</div>
                  </div>
                  <div className="detail-item">
                    <div className="detail-item-label">Contractor Email</div>
                    <div className="detail-item-value">{defect.contractor.email}</div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Contractor Actions - Reassign and Send Reminder */}
          {isApprover && defect.contractor && ['contractor_assigned', 'revision_requested'].includes(defect.status) && (
            <div className="detail-section">
              <div className="detail-section-title">Contractor Actions</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Send Reminder Button */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <button
                    className="secondary-btn"
                    onClick={async () => {
                      setSendingReminder(true);
                      await onSendReminder(defect.id);
                      setSendingReminder(false);
                    }}
                    disabled={sendingReminder}
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <Icons.Send /> {sendingReminder ? 'Sending...' : 'Send Quote Reminder'}
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Sends a reminder email to {defect.contractor.email}
                  </span>
                </div>

                {/* Reassign Contractor */}
                {showReassignSelect ? (
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                    <select
                      className="form-select"
                      value={reassignContractor}
                      onChange={(e) => setReassignContractor(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a different contractor...</option>
                      {contractors.filter(c => c.id !== defect.contractor.id).map(c => (
                        <option key={c.id} value={c.id}>{c.name}{c.specialty ? ` - ${c.specialty}` : ''}</option>
                      ))}
                    </select>
                    <button
                      className="primary-btn"
                      onClick={() => {
                        if (reassignContractor) {
                          const contractor = contractors.find(c => c.id === parseInt(reassignContractor));
                          onReassignContractor(defect.id, contractor);
                        }
                      }}
                      disabled={!reassignContractor}
                    >
                      Reassign
                    </button>
                    <button
                      className="secondary-btn"
                      onClick={() => {
                        setShowReassignSelect(false);
                        setReassignContractor('');
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <div>
                    <button
                      className="secondary-btn"
                      onClick={() => setShowReassignSelect(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                    >
                      <Icons.Refresh /> Change Contractor
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {defect.quote && (
            <div className="detail-section">
              <div className="detail-section-title">Quote</div>
              <div className="quote-card">
                <div className="quote-amount">£{defect.quote.amount.toLocaleString()}</div>
                <div className="quote-description">{defect.quote.description}</div>
                <div className="quote-meta">Submitted on {defect.quote.submitted}</div>
                {defect.quote.estimatedCompletionDate && (
                  <div className="quote-meta" style={{ marginTop: 4 }}>Estimated completion: {defect.quote.estimatedCompletionDate}</div>
                )}
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-section-title">Workflow Progress</div>
            <div className="workflow-timeline">
              {workflowSteps.map((step, index) => (
                <div key={index} className="timeline-item">
                  <div className={`timeline-dot ${step.rejected ? 'rejected' : step.completed ? 'completed' : index === activeStepIndex ? 'active' : ''}`}></div>
                  <div className="timeline-content">
                    <div className="timeline-title" style={step.rejected ? { color: 'var(--accent-red)' } : {}}>{step.label}</div>
                    <div className="timeline-meta">
                      {step.rejected ? (
                        <>
                          {step.date && `Rejected ${step.date}`}
                          {step.approver && ` by ${step.approver}`}
                        </>
                      ) : step.completed ? (
                        <>
                          {step.date && `Completed ${step.date}`}
                          {step.approver && ` by ${step.approver}`}
                          {step.contractor && `Assigned to ${step.contractor}`}
                          {step.amount && `Amount: £${step.amount.toLocaleString()}`}
                        </>
                      ) : index === activeStepIndex ? 'Awaiting action' : 'Pending'}
                    </div>
                    {step.rejectionReason && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: 12, 
                        background: 'rgba(255, 69, 58, 0.1)', 
                        border: '1px solid rgba(255, 69, 58, 0.3)',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 13,
                        color: 'var(--accent-red)'
                      }}>
                        <strong>Rejection Reason:</strong> {step.rejectionReason}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Action Buttons based on status - only show to approvers */}
          {isApprover && defect.status === 'pending_approval' && (
            <div className="action-buttons">
              <button className="approve-btn" onClick={() => openApproveModal('defect')}>
                <Icons.Check /> Approve Defect
              </button>
              <button className="approve-bypass-btn" onClick={() => openApproveModal('bypass')}>
                <Icons.Check /> Approve & Bypass Quote
              </button>
              <button className="reject-btn" onClick={() => setShowRejectModal(true)}>Reject</button>
            </div>
          )}

          {isApprover && defect.status === 'contractor_assigned' && !defect.contractor && (
            <div className="detail-section">
              <div className="detail-section-title">Assign Contractor</div>
              {showContractorSelect ? (
                <div style={{ display: 'flex', gap: 12 }}>
                  <select
                    className="form-select"
                    value={selectedContractor}
                    onChange={(e) => setSelectedContractor(e.target.value)}
                    style={{ flex: 1 }}
                  >
                    <option value="">Select a contractor...</option>
                    {contractors.map(c => (
                      <option key={c.id} value={c.id}>{c.name} - {c.specialty}</option>
                    ))}
                  </select>
                  <button className="primary-btn" onClick={handleAssignContractor}>
                    Assign & Send Email
                  </button>
                </div>
              ) : (
                <button className="primary-btn" onClick={() => setShowContractorSelect(true)}>
                  Select Contractor
                </button>
              )}
            </div>
          )}

          {isApprover && defect.status === 'quote_received' && (
            <div className="action-buttons">
              <button className="approve-btn" onClick={() => onApprove(defect.id, 'quote')}>
                <Icons.Check /> Approve Quote
              </button>
              <button className="revision-btn" onClick={() => setShowRevisionModal(true)}>Request Revision</button>
              <button className="reject-btn" onClick={() => setShowRejectModal(true)}>Reject</button>
            </div>
          )}

          {/* Manual Complete - available at any stage */}
          {isApprover && !['completed', 'rejected'].includes(defect.status) && (
            <div className="detail-section">
              <div className="detail-section-title">Manual Complete</div>
              <button
                className="approve-btn"
                onClick={() => onManualComplete(defect.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 8 }}
              >
                <Icons.Check /> Mark as Complete
              </button>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                Use this to manually complete the defect at any stage, bypassing the normal workflow.
              </p>
            </div>
          )}
        </div>

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="modal-body" style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
            <div className="detail-section" style={{ marginBottom: 0 }}>
              <div className="detail-section-title" style={{ color: 'var(--accent-red)' }}>
                {defect.status === 'quote_received' ? 'Reject Quote' : 'Reject Defect'}
              </div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Please provide a reason for rejection. This will be recorded and the {defect.status === 'quote_received' ? 'contractor' : 'reporter'} will be notified.
              </p>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Rejection Reason *</label>
                <textarea
                  className="form-textarea"
                  placeholder="Enter the reason for rejection..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  style={{ minHeight: 100 }}
                />
              </div>
              <div className="action-buttons" style={{ marginTop: 0 }}>
                <button className="secondary-btn" onClick={() => setShowRejectModal(false)}>Cancel</button>
                <button 
                  className="reject-btn" 
                  onClick={handleReject}
                  disabled={!rejectReason.trim()}
                  style={{ opacity: rejectReason.trim() ? 1 : 0.5 }}
                >
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Revision Modal */}
        {showRevisionModal && (
          <div className="modal-body" style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
            <div className="detail-section" style={{ marginBottom: 0 }}>
              <div className="detail-section-title" style={{ color: 'var(--accent-orange)' }}>Request Quote Revision</div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                Provide feedback to the contractor explaining what changes are needed to the quote.
              </p>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Revision Comments *</label>
                <textarea
                  className="form-textarea"
                  placeholder="E.g., Please provide a breakdown of materials vs labour costs, reduce scope to only essential repairs, clarify timeline..."
                  value={revisionComments}
                  onChange={(e) => setRevisionComments(e.target.value)}
                  style={{ minHeight: 100 }}
                />
              </div>
              <div className="action-buttons" style={{ marginTop: 0 }}>
                <button className="secondary-btn" onClick={() => setShowRevisionModal(false)}>Cancel</button>
                <button 
                  className="revision-btn" 
                  onClick={handleRequestRevision}
                  disabled={!revisionComments.trim()}
                  style={{ opacity: revisionComments.trim() ? 1 : 0.5 }}
                >
                  Send Revision Request
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Approve Modal */}
        {showApproveModal && (
          <div className="modal-body" style={{ borderTop: '1px solid var(--border-color)', background: 'var(--bg-tertiary)' }}>
            <div className="detail-section" style={{ marginBottom: 0 }}>
              <div className="detail-section-title" style={{ color: 'var(--accent-green)' }}>
                {approveType === 'bypass' ? 'Approve & Bypass Quote' : 'Approve Defect & Assign Contractor'}
              </div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
                {approveType === 'bypass' 
                  ? 'Select a contractor to assign. The defect will be approved and work can begin immediately without requiring a quote.'
                  : 'Select a contractor to assign. They will be notified and asked to provide a quote for the work.'
                }
              </p>
              <div className="form-group" style={{ marginBottom: 16 }}>
                <label className="form-label">Select Contractor *</label>
                <select
                  className="form-select"
                  value={approveContractor}
                  onChange={(e) => setApproveContractor(e.target.value)}
                >
                  <option value="">Select a contractor...</option>
                  {contractors.map(c => (
                    <option key={c.id} value={c.id}>{c.name} - {c.specialty}</option>
                  ))}
                </select>
              </div>
              <div className="action-buttons" style={{ marginTop: 0 }}>
                <button className="secondary-btn" onClick={() => setShowApproveModal(false)}>Cancel</button>
                <button 
                  className="approve-btn" 
                  onClick={handleApproveWithContractor}
                  disabled={!approveContractor}
                  style={{ opacity: approveContractor ? 1 : 0.5 }}
                >
                  <Icons.Check /> {approveType === 'bypass' ? 'Approve & Assign' : 'Approve & Assign Contractor'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Approvals Page
const ApprovalsPage = ({ defects, onSelectDefect }) => {
  const pendingDefects = defects.filter(d => d.status === 'pending_approval');
  const pendingQuotes = defects.filter(d => d.status === 'quote_received');

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Approvals</h1>
          <p className="page-subtitle">Review and approve defects and quotes</p>
        </div>
      </div>

      <div style={{ marginBottom: 40 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Pending Defect Approvals ({pendingDefects.length})
        </h2>
        {pendingDefects.length > 0 ? (
          <DefectsTable defects={pendingDefects} onSelectDefect={onSelectDefect} />
        ) : (
          <div className="defects-table">
            <div className="empty-state">
              <div className="empty-state-icon">✓</div>
              <div className="empty-state-title">All caught up!</div>
              <div className="empty-state-text">No defects pending approval</div>
            </div>
          </div>
        )}
      </div>

      <div>
        <h2 style={{ fontSize: 18, fontWeight: 600, marginBottom: 16 }}>
          Pending Quote Approvals ({pendingQuotes.length})
        </h2>
        {pendingQuotes.length > 0 ? (
          <DefectsTable defects={pendingQuotes} onSelectDefect={onSelectDefect} />
        ) : (
          <div className="defects-table">
            <div className="empty-state">
              <div className="empty-state-icon">💰</div>
              <div className="empty-state-title">No quotes to review</div>
              <div className="empty-state-text">Quotes will appear here when contractors submit them</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Contractors Page
const ContractorsPage = ({ contractors, onAddContractor, onRemoveContractor, onEditContractor }) => {
  const [newContractorName, setNewContractorName] = useState('');
  const [newContractorEmail, setNewContractorEmail] = useState('');
  const [newContractorPhone, setNewContractorPhone] = useState('');
  const [newContractorSpecialty, setNewContractorSpecialty] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);

  // Edit state
  const [editingContractor, setEditingContractor] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSpecialty, setEditSpecialty] = useState('');

  const handleAddContractor = (e) => {
    e.preventDefault();
    if (newContractorName.trim() && newContractorEmail.trim()) {
      onAddContractor({
        name: newContractorName.trim(),
        email: newContractorEmail.trim().toLowerCase(),
        phone: newContractorPhone.trim(),
        specialty: newContractorSpecialty.trim()
      });
      setNewContractorName('');
      setNewContractorEmail('');
      setNewContractorPhone('');
      setNewContractorSpecialty('');
      setShowAddForm(false);
    }
  };

  const handleStartEdit = (contractor) => {
    setEditingContractor(contractor);
    setEditName(contractor.name);
    setEditEmail(contractor.email);
    setEditPhone(contractor.phone || '');
    setEditSpecialty(contractor.specialty || '');
  };

  const handleEditContractor = (e) => {
    e.preventDefault();
    if (editName.trim() && editEmail.trim() && editingContractor) {
      onEditContractor(editingContractor.id, {
        name: editName.trim(),
        email: editEmail.trim().toLowerCase(),
        phone: editPhone.trim(),
        specialty: editSpecialty.trim()
      });
      setEditingContractor(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingContractor(null);
    setEditName('');
    setEditEmail('');
    setEditPhone('');
    setEditSpecialty('');
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Contractors</h1>
          <p className="page-subtitle">Manage approved contractors</p>
        </div>
        {!showAddForm && (
          <button className="primary-btn" onClick={() => setShowAddForm(true)}>
            <Icons.Plus />
            Add Contractor
          </button>
        )}
      </div>

      <div className="defects-table" style={{ padding: 24 }}>
        {showAddForm && (
          <form onSubmit={handleAddContractor} style={{ 
            background: 'var(--bg-tertiary)', 
            padding: 20, 
            borderRadius: 'var(--radius-md)',
            marginBottom: 20,
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Company Name *</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="ABC Repairs Ltd"
                  value={newContractorName}
                  onChange={(e) => setNewContractorName(e.target.value)}
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="jobs@abcrepairs.com"
                  value={newContractorEmail}
                  onChange={(e) => setNewContractorEmail(e.target.value)}
                  required
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Phone</label>
                <input
                  type="tel"
                  className="form-input"
                  placeholder="07123 456789"
                  value={newContractorPhone}
                  onChange={(e) => setNewContractorPhone(e.target.value)}
                />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Specialty</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. Plumbing, Electrical, General"
                  value={newContractorSpecialty}
                  onChange={(e) => setNewContractorSpecialty(e.target.value)}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 12 }}>
              <button type="submit" className="primary-btn" style={{ flex: 'none' }}>
                Add Contractor
              </button>
              <button 
                type="button" 
                className="reject-btn" 
                style={{ flex: 'none' }}
                onClick={() => {
                  setShowAddForm(false);
                  setNewContractorName('');
                  setNewContractorEmail('');
                  setNewContractorPhone('');
                  setNewContractorSpecialty('');
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {contractors.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">🔧</div>
            <div className="empty-state-title">No Contractors</div>
            <div className="empty-state-text">Add contractors to assign them to defects</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {contractors.map(contractor => (
              <div 
                key={contractor.id}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  background: 'var(--bg-tertiary)', 
                  padding: '16px 20px', 
                  borderRadius: 'var(--radius-sm)',
                  border: '1px solid var(--border-color)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 44,
                    height: 44,
                    borderRadius: '50%',
                    background: 'var(--accent-orange)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 600,
                    color: 'white'
                  }}>
                    {contractor.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{contractor.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                      {contractor.email}
                      {contractor.phone && <span> • {contractor.phone}</span>}
                    </div>
                    {contractor.specialty && (
                      <div style={{ 
                        fontSize: 12, 
                        color: 'var(--accent-blue)', 
                        marginTop: 4,
                        background: 'rgba(10, 132, 255, 0.1)',
                        padding: '2px 8px',
                        borderRadius: 4,
                        display: 'inline-block'
                      }}>
                        {contractor.specialty}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button
                    onClick={() => handleStartEdit(contractor)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 8,
                      borderRadius: 'var(--radius-sm)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-blue)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Edit contractor"
                  >
                    <Icons.Edit />
                  </button>
                  <button
                    onClick={() => onRemoveContractor(contractor.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 8,
                      borderRadius: 'var(--radius-sm)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-red)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Remove contractor"
                  >
                    <Icons.Close />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Edit Contractor Modal */}
        {editingContractor && (
          <div
            className="modal-overlay"
            onClick={handleCancelEdit}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'var(--bg-primary)',
                borderRadius: 'var(--radius-lg)',
                padding: 24,
                width: '100%',
                maxWidth: 500,
                border: '1px solid var(--border-color)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>Edit Contractor</h3>
                <button
                  onClick={handleCancelEdit}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--text-muted)',
                    padding: 4
                  }}
                >
                  <Icons.Close />
                </button>
              </div>
              <form onSubmit={handleEditContractor}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Company Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Email *</label>
                    <input
                      type="email"
                      className="form-input"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={editPhone}
                      onChange={(e) => setEditPhone(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 0 }}>
                    <label className="form-label">Specialty</label>
                    <input
                      type="text"
                      className="form-input"
                      value={editSpecialty}
                      onChange={(e) => setEditSpecialty(e.target.value)}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  <button type="button" className="reject-btn" onClick={handleCancelEdit}>
                    Cancel
                  </button>
                  <button type="submit" className="primary-btn">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Reports Page
const ReportsPage = ({ defects, stores }) => {
  const classifications = useContext(ClassificationsContext);
  const [locationFilter, setLocationFilter] = useState('all');
  const [classificationFilter, setClassificationFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Includes removed classifications so older defects can still be filtered
  const classificationOptions = classifications.map(c => ({
    value: c.key,
    label: c.active === 1 ? c.name : `${c.name} (removed)`
  }));

  const priorityOptions = [
    { value: 'urgent', label: 'Urgent' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' }
  ];

  const statusOptions = [
    { value: 'pending_approval', label: 'Pending Approval' },
    { value: 'contractor_assigned', label: 'Contractor Assigned' },
    { value: 'quote_received', label: 'Quote Received' },
    { value: 'quote_approved', label: 'Quote Approved' },
    { value: 'revision_requested', label: 'Revision Requested' },
    { value: 'completed', label: 'Completed' },
    { value: 'rejected', label: 'Rejected' }
  ];

  // Filter defects based on all filters
  const filteredDefects = defects.filter(defect => {
    const matchesLocation = locationFilter === 'all' || defect.location === locationFilter;
    const matchesClassification = classificationFilter === 'all' || defect.classification === classificationFilter;
    const matchesPriority = priorityFilter === 'all' || defect.severity === priorityFilter;
    const matchesStatus = statusFilter === 'all' || defect.status === statusFilter;
    
    let matchesDateFrom = true;
    let matchesDateTo = true;
    if (dateFrom && defect.reportedDate) {
      matchesDateFrom = defect.reportedDate >= dateFrom;
    }
    if (dateTo && defect.reportedDate) {
      matchesDateTo = defect.reportedDate <= dateTo;
    }
    
    return matchesLocation && matchesClassification && matchesPriority && matchesStatus && matchesDateFrom && matchesDateTo;
  });

  const formatClassification = (key) => classificationLabel(classifications, key);

  const formatStatus = (status) => {
    const labels = {
      pending_approval: 'Pending Approval',
      contractor_assigned: 'Contractor Assigned',
      quote_received: 'Quote Received',
      quote_approved: 'Quote Approved',
      revision_requested: 'Revision Requested',
      rejected: 'Rejected',
      completed: 'Completed'
    };
    return labels[status] || status;
  };

  const formatPriority = (severity) => {
    const labels = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low' };
    return labels[severity] || severity;
  };

  // Export to CSV
  const exportToCSV = () => {
    const headers = ['ID', 'Title', 'Description', 'Location', 'Classification', 'Priority', 'Status', 'Reported By', 'Reporter Name', 'Report Date', 'Contractor', 'Quote Amount', 'Est. Completion', 'Completed Date'];

    const rows = filteredDefects.map(d => [
      d.id,
      `"${(d.title || '').replace(/"/g, '""')}"`,
      `"${(d.description || '').replace(/"/g, '""')}"`,
      d.location || '',
      formatClassification(d.classification),
      formatPriority(d.severity),
      formatStatus(d.status),
      d.reportedBy || '',
      d.reporterName || '',
      d.reportedDate || '',
      d.contractor?.name || '',
      d.quote?.amount || '',
      d.quote?.estimatedCompletionDate || '',
      d.completedDate || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `defects_report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Export to Excel (using CSV with .xls extension for compatibility)
  const exportToExcel = () => {
    const headers = ['ID', 'Title', 'Description', 'Location', 'Classification', 'Priority', 'Status', 'Reported By', 'Reporter Name', 'Report Date', 'Contractor', 'Quote Amount', 'Est. Completion', 'Completed Date'];

    let tableHtml = '<table border="1"><tr>';
    headers.forEach(h => { tableHtml += `<th style="background:#333;color:#fff;padding:8px;">${h}</th>`; });
    tableHtml += '</tr>';

    filteredDefects.forEach(d => {
      tableHtml += '<tr>';
      tableHtml += `<td>${d.id}</td>`;
      tableHtml += `<td>${d.title || ''}</td>`;
      tableHtml += `<td>${d.description || ''}</td>`;
      tableHtml += `<td>${d.location || ''}</td>`;
      tableHtml += `<td>${formatClassification(d.classification)}</td>`;
      tableHtml += `<td>${formatPriority(d.severity)}</td>`;
      tableHtml += `<td>${formatStatus(d.status)}</td>`;
      tableHtml += `<td>${d.reportedBy || ''}</td>`;
      tableHtml += `<td>${d.reporterName || ''}</td>`;
      tableHtml += `<td>${d.reportedDate || ''}</td>`;
      tableHtml += `<td>${d.contractor?.name || ''}</td>`;
      tableHtml += `<td>${d.quote?.amount ? '£' + d.quote.amount : ''}</td>`;
      tableHtml += `<td>${d.quote?.estimatedCompletionDate || ''}</td>`;
      tableHtml += `<td>${d.completedDate || ''}</td>`;
      tableHtml += '</tr>';
    });
    tableHtml += '</table>';
    
    const blob = new Blob([`<html><head><meta charset="UTF-8"></head><body>${tableHtml}</body></html>`], 
      { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `defects_report_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  // Export to PDF (using print-friendly HTML)
  const exportToPDF = () => {
    const printWindow = window.open('', '_blank');
    
    let tableHtml = `
      <html>
      <head>
        <title>Defects Report - ${new Date().toLocaleDateString()}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { color: #333; margin-bottom: 10px; }
          .meta { color: #666; margin-bottom: 20px; font-size: 14px; }
          table { width: 100%; border-collapse: collapse; font-size: 11px; }
          th { background: #333; color: white; padding: 8px; text-align: left; }
          td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
          tr:nth-child(even) { background: #f9f9f9; }
          .urgent { color: #dc3545; font-weight: bold; }
          .high { color: #fd7e14; }
          .medium { color: #0d6efd; }
          .low { color: #198754; }
          .completed { color: #198754; }
          .rejected { color: #dc3545; }
          @media print {
            body { padding: 0; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>
        <h1>Defects Report</h1>
        <div class="meta">
          Generated: ${new Date().toLocaleString()}<br>
          Total Records: ${filteredDefects.length}
          ${locationFilter !== 'all' ? `<br>Location: ${locationFilter}` : ''}
          ${classificationFilter !== 'all' ? `<br>Classification: ${formatClassification(classificationFilter)}` : ''}
          ${priorityFilter !== 'all' ? `<br>Priority: ${formatPriority(priorityFilter)}` : ''}
          ${statusFilter !== 'all' ? `<br>Status: ${formatStatus(statusFilter)}` : ''}
          ${dateFrom ? `<br>From: ${dateFrom}` : ''}
          ${dateTo ? `<br>To: ${dateTo}` : ''}
        </div>
        <table>
          <tr>
            <th>ID</th>
            <th>Title</th>
            <th>Location</th>
            <th>Classification</th>
            <th>Priority</th>
            <th>Status</th>
            <th>Reported By</th>
            <th>Date</th>
            <th>Contractor</th>
            <th>Quote</th>
            <th>Est. Completion</th>
          </tr>
    `;

    filteredDefects.forEach(d => {
      tableHtml += `
        <tr>
          <td>${d.id}</td>
          <td>${d.title || ''}</td>
          <td>${d.location || ''}</td>
          <td>${formatClassification(d.classification)}</td>
          <td class="${d.severity}">${formatPriority(d.severity)}</td>
          <td class="${d.status === 'completed' ? 'completed' : d.status === 'rejected' ? 'rejected' : ''}">${formatStatus(d.status)}</td>
          <td>${d.reportedBy || ''}${d.reporterName ? ` (${d.reporterName})` : ''}</td>
          <td>${d.reportedDate || ''}</td>
          <td>${d.contractor?.name || '-'}</td>
          <td>${d.quote?.amount ? '£' + d.quote.amount.toLocaleString() : '-'}</td>
          <td>${d.quote?.estimatedCompletionDate || '-'}</td>
        </tr>
      `;
    });
    
    tableHtml += '</table></body></html>';
    
    printWindow.document.write(tableHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { printWindow.print(); }, 250);
  };

  const clearFilters = () => {
    setLocationFilter('all');
    setClassificationFilter('all');
    setPriorityFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = locationFilter !== 'all' || classificationFilter !== 'all' || 
    priorityFilter !== 'all' || statusFilter !== 'all' || dateFrom || dateTo;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="page-subtitle">Filter and export defect data</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="secondary-btn" onClick={exportToCSV} disabled={filteredDefects.length === 0}>
            <Icons.Download /> CSV
          </button>
          <button className="secondary-btn" onClick={exportToExcel} disabled={filteredDefects.length === 0}>
            <Icons.Download /> Excel
          </button>
          <button className="primary-btn" onClick={exportToPDF} disabled={filteredDefects.length === 0}>
            <Icons.Download /> PDF
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="defects-table" style={{ padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div className="detail-section-title" style={{ margin: 0 }}>Filters</div>
          {hasActiveFilters && (
            <button 
              className="secondary-btn" 
              style={{ padding: '6px 12px', fontSize: 12 }}
              onClick={clearFilters}
            >
              Clear All Filters
            </button>
          )}
        </div>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 }}>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Location</label>
            <select
              className="form-select"
              value={locationFilter}
              onChange={(e) => setLocationFilter(e.target.value)}
            >
              <option value="all">All Locations</option>
              {stores && stores.map(store => (
                <option key={store.id} value={store.name}>{store.name}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Classification</label>
            <select
              className="form-select"
              value={classificationFilter}
              onChange={(e) => setClassificationFilter(e.target.value)}
            >
              <option value="all">All Classifications</option>
              {classificationOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Priority</label>
            <select
              className="form-select"
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
            >
              <option value="all">All Priorities</option>
              {priorityOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Statuses</option>
              {statusOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date From</label>
            <input
              type="date"
              className="form-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Date To</label>
            <input
              type="date"
              className="form-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: 16,
        padding: '12px 16px',
        background: 'var(--bg-secondary)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--border-color)'
      }}>
        <span style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          Showing <strong style={{ color: 'var(--text-primary)' }}>{filteredDefects.length}</strong> of {defects.length} defects
        </span>
        <div style={{ display: 'flex', gap: 16, fontSize: 13 }}>
          <span style={{ color: 'var(--accent-green)' }}>
            ✓ {filteredDefects.filter(d => d.status === 'completed').length} Completed
          </span>
          <span style={{ color: 'var(--accent-orange)' }}>
            ⏳ {filteredDefects.filter(d => !['completed', 'rejected'].includes(d.status)).length} In Progress
          </span>
          <span style={{ color: 'var(--accent-red)' }}>
            ✕ {filteredDefects.filter(d => d.status === 'rejected').length} Rejected
          </span>
        </div>
      </div>

      {/* Results Table */}
      <div className="defects-table">
        {filteredDefects.length > 0 ? (
          <>
            <div className="table-header" style={{ gridTemplateColumns: '90px 2fr 100px 110px 90px 120px 100px' }}>
              <div>ID</div>
              <div>Title</div>
              <div>Location</div>
              <div>Classification</div>
              <div>Priority</div>
              <div>Status</div>
              <div>Date</div>
            </div>
            {filteredDefects.map(defect => (
              <div 
                key={defect.id} 
                className="table-row"
                style={{ gridTemplateColumns: '90px 2fr 100px 110px 90px 120px 100px' }}
              >
                <div className="defect-id">{defect.id}</div>
                <div>
                  <div className="defect-title">{defect.title}</div>
                  <div className="defect-store">{defect.reportedBy}</div>
                </div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>{defect.location}</div>
                <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                  {formatClassification(defect.classification)}
                </div>
                <div>
                  <span className={`severity-badge ${defect.severity}`}>
                    <span className="severity-dot"></span>
                    {formatPriority(defect.severity)}
                  </span>
                </div>
                <div>
                  <span className={`status-badge ${defect.status}`}>
                    {formatStatus(defect.status)}
                  </span>
                </div>
                <div className="table-date">{defect.reportedDate}</div>
              </div>
            ))}
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">📊</div>
            <div className="empty-state-title">No Defects Found</div>
            <div className="empty-state-text">
              {hasActiveFilters 
                ? 'Try adjusting your filters to see more results' 
                : 'No defects have been reported yet'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Settings Page
const SettingsPage = ({ approvers, onAddApprover, onRemoveApprover, stores, onAddStore, onRemoveStore, onAddClassification, onRemoveClassification }) => {
  const classifications = useContext(ClassificationsContext);
  const activeClassifications = classifications.filter(c => c.active === 1);
  const [newClassificationName, setNewClassificationName] = useState('');
  const [showAddClassificationForm, setShowAddClassificationForm] = useState(false);
  const [newApproverName, setNewApproverName] = useState('');
  const [newApproverEmail, setNewApproverEmail] = useState('');
  const [showAddApproverForm, setShowAddApproverForm] = useState(false);
  
  const [newStoreName, setNewStoreName] = useState('');
  const [showAddStoreForm, setShowAddStoreForm] = useState(false);

  const handleAddApprover = (e) => {
    e.preventDefault();
    if (newApproverName.trim() && newApproverEmail.trim()) {
      onAddApprover({
        id: Date.now(),
        name: newApproverName.trim(),
        email: newApproverEmail.trim().toLowerCase()
      });
      setNewApproverName('');
      setNewApproverEmail('');
      setShowAddApproverForm(false);
    }
  };

  const handleAddStore = (e) => {
    e.preventDefault();
    if (newStoreName.trim()) {
      onAddStore({
        name: newStoreName.trim()
      });
      setNewStoreName('');
      setShowAddStoreForm(false);
    }
  };

  const handleAddClassification = (e) => {
    e.preventDefault();
    if (newClassificationName.trim()) {
      onAddClassification({
        name: newClassificationName.trim()
      });
      setNewClassificationName('');
      setShowAddClassificationForm(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>
          <p className="page-subtitle">Configure system preferences</p>
        </div>
      </div>

      <div className="defects-table" style={{ padding: 24 }}>
        {/* Stores Section */}
        <div className="detail-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div className="detail-section-title" style={{ marginBottom: 4 }}>Stores</div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                Manage store locations that can be selected when reporting defects.
              </p>
            </div>
            {!showAddStoreForm && (
              <button className="primary-btn" onClick={() => setShowAddStoreForm(true)}>
                + Add Store
              </button>
            )}
          </div>

          {showAddStoreForm && (
            <form onSubmit={handleAddStore} style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 20, 
              borderRadius: 'var(--radius-md)',
              marginBottom: 20,
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Store Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Norwich, Ipswich, Cambridge"
                    value={newStoreName}
                    onChange={(e) => setNewStoreName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="primary-btn" style={{ flex: 'none' }}>
                  Add Store
                </button>
                <button 
                  type="button" 
                  className="reject-btn" 
                  style={{ flex: 'none' }}
                  onClick={() => {
                    setShowAddStoreForm(false);
                    setNewStoreName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {stores.length === 0 ? (
            <div style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 32, 
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <p style={{ margin: 0 }}>No stores configured. Add stores for defect reporting locations.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {stores.map(store => (
                <div 
                  key={store.id}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    background: 'var(--bg-tertiary)', 
                    padding: '8px 12px', 
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{store.name}</span>
                  <button 
                    onClick={() => onRemoveStore(store.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 4,
                      borderRadius: 'var(--radius-sm)',
                      transition: 'all 0.15s ease',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-red)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Remove store"
                  >
                    <Icons.Close />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Classifications Section */}
        <div className="detail-section" style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div className="detail-section-title" style={{ marginBottom: 4 }}>Classifications</div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                Manage the classifications that can be selected when reporting defects. Removing one keeps it on existing defects.
              </p>
            </div>
            {!showAddClassificationForm && (
              <button className="primary-btn" onClick={() => setShowAddClassificationForm(true)}>
                + Add Classification
              </button>
            )}
          </div>

          {showAddClassificationForm && (
            <form onSubmit={handleAddClassification} style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 20, 
              borderRadius: 'var(--radius-md)',
              marginBottom: 20,
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Classification Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. Signage, Heating, Doors & Windows"
                    value={newClassificationName}
                    onChange={(e) => setNewClassificationName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="primary-btn" style={{ flex: 'none' }}>
                  Add Classification
                </button>
                <button 
                  type="button" 
                  className="reject-btn" 
                  style={{ flex: 'none' }}
                  onClick={() => {
                    setShowAddClassificationForm(false);
                    setNewClassificationName('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {activeClassifications.length === 0 ? (
            <div style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 32, 
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <p style={{ margin: 0 }}>No classifications configured. Add classifications for defect reporting.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {activeClassifications.map(classification => (
                <div 
                  key={classification.id}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 8,
                    background: 'var(--bg-tertiary)', 
                    padding: '8px 12px', 
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <span style={{ fontSize: 14, fontWeight: 500 }}>{classification.name}</span>
                  {activeClassifications.length > 1 && (
                    <button 
                      onClick={() => onRemoveClassification(classification.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-muted)',
                        cursor: 'pointer',
                        padding: 4,
                        borderRadius: 'var(--radius-sm)',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-red)'}
                      onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                      title="Remove classification"
                    >
                      <Icons.Close />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Approvers Section */}
        <div className="detail-section" style={{ marginTop: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <div className="detail-section-title" style={{ marginBottom: 4 }}>Approvers</div>
              <p style={{ fontSize: 14, color: 'var(--text-secondary)', margin: 0 }}>
                People who receive notifications and can approve defects and quotes.
              </p>
            </div>
            {!showAddApproverForm && (
              <button className="primary-btn" onClick={() => setShowAddApproverForm(true)}>
                + Add Approver
              </button>
            )}
          </div>

          {showAddApproverForm && (
            <form onSubmit={handleAddApprover} style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 20, 
              borderRadius: 'var(--radius-md)',
              marginBottom: 20,
              border: '1px solid var(--border-color)'
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Name</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="John Smith"
                    value={newApproverName}
                    onChange={(e) => setNewApproverName(e.target.value)}
                    required
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="john@gouldbarbers.co.uk"
                    value={newApproverEmail}
                    onChange={(e) => setNewApproverEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="primary-btn" style={{ flex: 'none' }}>
                  Add Approver
                </button>
                <button 
                  type="button" 
                  className="reject-btn" 
                  style={{ flex: 'none' }}
                  onClick={() => {
                    setShowAddApproverForm(false);
                    setNewApproverName('');
                    setNewApproverEmail('');
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          {approvers.length === 0 ? (
            <div style={{ 
              background: 'var(--bg-tertiary)', 
              padding: 32, 
              borderRadius: 'var(--radius-md)',
              textAlign: 'center',
              color: 'var(--text-muted)'
            }}>
              <p style={{ margin: 0 }}>No approvers configured. Add approvers to receive email notifications.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {approvers.map(approver => (
                <div 
                  key={approver.id}
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    background: 'var(--bg-tertiary)', 
                    padding: '12px 16px', 
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      background: 'var(--accent-blue)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 14,
                      fontWeight: 600,
                      color: 'white'
                    }}>
                      {approver.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 14 }}>{approver.name}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{approver.email}</div>
                    </div>
                  </div>
                  <button 
                    onClick={() => onRemoveApprover(approver.id)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: 'var(--text-muted)',
                      cursor: 'pointer',
                      padding: 8,
                      borderRadius: 'var(--radius-sm)',
                      transition: 'all 0.15s ease'
                    }}
                    onMouseOver={(e) => e.currentTarget.style.color = 'var(--accent-red)'}
                    onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                    title="Remove approver"
                  >
                    <Icons.Close />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Email Notifications Section */}
        <div className="detail-section" style={{ marginTop: 32 }}>
          <div className="detail-section-title">Email Notifications</div>
          <div className="detail-description">
            <p style={{ marginBottom: 12 }}>Emails are automatically sent at these workflow stages:</p>
            <ul style={{ margin: 0, paddingLeft: 20, lineHeight: 1.8 }}>
              <li><strong>New defect reported</strong> → Submitter + all approvers</li>
              <li><strong>Defect approved</strong> → Submitter</li>
              <li><strong>Quote submitted</strong> → All approvers</li>
              <li><strong>Job completed</strong> → Submitter + all approvers</li>
            </ul>
          </div>
        </div>
        
        {/* Microsoft 365 Section */}
        <div className="detail-section" style={{ marginTop: 32 }}>
          <div className="detail-section-title">Microsoft 365 Integration</div>
          <div className="detail-description">
            <p style={{ marginBottom: 8 }}>SSO authentication is configured via Microsoft Entra ID (Azure AD).</p>
            <p style={{ margin: 0, color: 'var(--text-muted)' }}>
              Emails are sent from: <strong>defecttrack@gouldbarbers.co.uk</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contractor Portal Page
const ContractorPortalPage = ({ defects, onSubmitQuote, onCompleteJob }) => {
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [activeTab, setActiveTab] = useState('pending_quote');

  // Filter defects assigned to contractor that need quotes or are in progress
  const pendingQuoteDefects = defects.filter(d => 
    d.contractor && d.status === 'contractor_assigned' && !d.quote
  );
  
  const awaitingApprovalDefects = defects.filter(d => 
    d.contractor && d.status === 'quote_received'
  );
  
  const approvedJobsDefects = defects.filter(d => 
    d.contractor && d.approvals.some(a => a.type === 'quote') && d.status !== 'completed'
  );

  const completedDefects = defects.filter(d => 
    d.contractor && d.status === 'completed'
  );

  const getTabDefects = () => {
    switch (activeTab) {
      case 'pending_quote': return pendingQuoteDefects;
      case 'awaiting_approval': return awaitingApprovalDefects;
      case 'approved_jobs': return approvedJobsDefects;
      case 'completed': return completedDefects;
      default: return [];
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Contractor Portal</h1>
          <p className="page-subtitle">View assigned defects, submit quotes, and complete jobs</p>
        </div>
      </div>

      <div className="contractor-tabs">
        <button 
          className={`contractor-tab ${activeTab === 'pending_quote' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending_quote')}
        >
          Pending Quote
          {pendingQuoteDefects.length > 0 && (
            <span className="tab-badge">{pendingQuoteDefects.length}</span>
          )}
        </button>
        <button 
          className={`contractor-tab ${activeTab === 'awaiting_approval' ? 'active' : ''}`}
          onClick={() => setActiveTab('awaiting_approval')}
        >
          Awaiting Approval
          {awaitingApprovalDefects.length > 0 && (
            <span className="tab-badge yellow">{awaitingApprovalDefects.length}</span>
          )}
        </button>
        <button 
          className={`contractor-tab ${activeTab === 'approved_jobs' ? 'active' : ''}`}
          onClick={() => setActiveTab('approved_jobs')}
        >
          Approved Jobs
          {approvedJobsDefects.length > 0 && (
            <span className="tab-badge green">{approvedJobsDefects.length}</span>
          )}
        </button>
        <button 
          className={`contractor-tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed
        </button>
      </div>

      <div className="contractor-defects-list">
        {getTabDefects().length > 0 ? (
          getTabDefects().map(defect => (
            <div 
              key={defect.id} 
              className="contractor-defect-card"
              onClick={() => setSelectedDefect(defect)}
            >
              <div className="contractor-defect-header">
                <span className="defect-id">{defect.id}</span>
                <span className={`severity-badge ${defect.severity}`}>
                  <span className="severity-dot"></span>
                  {defect.severity.charAt(0).toUpperCase() + defect.severity.slice(1)}
                </span>
              </div>
              <h3 className="contractor-defect-title">{defect.title}</h3>
              <p className="contractor-defect-store">Store: {defect.location}</p>
              <p className="contractor-defect-description">{defect.description}</p>
              {defect.quote && (
                <div className="contractor-defect-quote">
                  <span className="quote-label">Your Quote:</span>
                  <span className="quote-value">£{defect.quote.amount.toLocaleString()}</span>
                </div>
              )}
              <div className="contractor-defect-footer">
                <span className="contractor-defect-date">Reported: {defect.reportedDate}</span>
                <button className="view-details-btn">
                  {activeTab === 'pending_quote' ? 'Submit Quote' : 
                   activeTab === 'approved_jobs' ? 'Complete Job' : 'View Details'}
                  <span>→</span>
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="defects-table">
            <div className="empty-state">
              <div className="empty-state-icon">
                {activeTab === 'pending_quote' ? '📋' : 
                 activeTab === 'awaiting_approval' ? '⏳' :
                 activeTab === 'approved_jobs' ? '🔧' : '✓'}
              </div>
              <div className="empty-state-title">
                {activeTab === 'pending_quote' ? 'No defects awaiting quote' : 
                 activeTab === 'awaiting_approval' ? 'No quotes awaiting approval' :
                 activeTab === 'approved_jobs' ? 'No approved jobs to complete' : 'No completed jobs yet'}
              </div>
              <div className="empty-state-text">
                {activeTab === 'pending_quote' ? 'New assignments will appear here' : 
                 activeTab === 'awaiting_approval' ? 'Submitted quotes will appear here' :
                 activeTab === 'approved_jobs' ? 'Approved quotes will appear here for completion' : 'Completed jobs will be shown here'}
              </div>
            </div>
          </div>
        )}
      </div>

      {selectedDefect && (
        <ContractorDefectModal
          defect={selectedDefect}
          activeTab={activeTab}
          onClose={() => setSelectedDefect(null)}
          onSubmitQuote={onSubmitQuote}
          onCompleteJob={onCompleteJob}
        />
      )}
    </div>
  );
};

// Contractor Defect Modal
const ContractorDefectModal = ({ defect, activeTab, onClose, onSubmitQuote, onCompleteJob }) => {
  const [quoteAmount, setQuoteAmount] = useState('');
  const [quoteDescription, setQuoteDescription] = useState('');
  const [estimatedCompletionDate, setEstimatedCompletionDate] = useState('');
  const [completionNotes, setCompletionNotes] = useState('');
  const [completionImages, setCompletionImages] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleSubmitQuote = (e) => {
    e.preventDefault();
    onSubmitQuote(defect.id, {
      amount: parseFloat(quoteAmount),
      description: quoteDescription,
      estimatedCompletionDate: estimatedCompletionDate || null,
      submitted: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  const handleCompleteJob = (e) => {
    e.preventDefault();
    onCompleteJob(defect.id, {
      notes: completionNotes,
      images: completionImages,
      completedDate: new Date().toISOString().split('T')[0]
    });
    onClose();
  };

  const handleFiles = async (files) => {
    const validFiles = Array.from(files).filter(file =>
      file.type.startsWith('image/')
    );

    for (const file of validFiles) {
      const compressed = await compressImage(file);
      setCompletionImages(prev => [...prev, compressed]);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const removeImage = (imageId) => {
    setCompletionImages(prev => prev.filter(img => img.id !== imageId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 700 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <span className="defect-id" style={{ marginRight: 12 }}>{defect.id}</span>
            <span className={`severity-badge ${defect.severity}`}>
              <span className="severity-dot"></span>
              {defect.severity.charAt(0).toUpperCase() + defect.severity.slice(1)}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>
            <Icons.Close />
          </button>
        </div>
        <div className="modal-body">
          <h2 style={{ fontSize: 20, fontWeight: 600, marginBottom: 8 }}>{defect.title}</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 24 }}>Store: {defect.location}</p>
          
          <div className="detail-section">
            <div className="detail-section-title">Description</div>
            <div className="detail-description">{defect.description}</div>
          </div>

          {defect.images && defect.images.length > 0 && (
            <div className="detail-section">
              <div className="detail-section-title">Defect Images ({defect.images.length})</div>
              <div className="detail-images-grid">
                {defect.images.map((image, index) => (
                  <div key={image.id || index} className="detail-image-item">
                    <img src={image.url} alt={image.name || `Image ${index + 1}`} />
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="detail-section">
            <div className="detail-section-title">Details</div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-item-label">Reported By</div>
                <div className="detail-item-value">{defect.reportedBy}</div>
              </div>
              <div className="detail-item">
                <div className="detail-item-label">Report Date</div>
                <div className="detail-item-value">{defect.reportedDate}</div>
              </div>
            </div>
          </div>

          {/* Submit Quote Form */}
          {activeTab === 'pending_quote' && (
            <form onSubmit={handleSubmitQuote}>
              <div className="detail-section">
                <div className="detail-section-title">Submit Your Quote</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Quote Amount (£) *</label>
                    <input
                      type="number"
                      className="form-input"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      value={quoteAmount}
                      onChange={(e) => setQuoteAmount(e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Estimated Completion Date</label>
                  <input
                    type="date"
                    className="form-input"
                    value={estimatedCompletionDate}
                    onChange={(e) => setEstimatedCompletionDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description of Work *</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe the work to be carried out, materials needed, etc."
                    value={quoteDescription}
                    onChange={(e) => setQuoteDescription(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="action-buttons">
                <button type="submit" className="approve-btn" style={{ flex: 'none', minWidth: 200 }}>
                  Submit Quote
                </button>
              </div>
            </form>
          )}

          {/* Awaiting Approval View */}
          {activeTab === 'awaiting_approval' && defect.quote && (
            <div className="detail-section">
              <div className="detail-section-title">Your Submitted Quote</div>
              <div className="quote-card">
                <div className="quote-amount">£{defect.quote.amount.toLocaleString()}</div>
                <div className="quote-description">{defect.quote.description}</div>
                <div className="quote-meta">Submitted on {defect.quote.submitted}</div>
                {defect.quote.estimatedCompletionDate && (
                  <div className="quote-meta" style={{ marginTop: 4 }}>Estimated completion: {defect.quote.estimatedCompletionDate}</div>
                )}
                <div className="quote-status-banner">
                  <span className="status-icon">⏳</span>
                  Awaiting client approval
                </div>
              </div>
            </div>
          )}

          {/* Complete Job Form */}
          {activeTab === 'approved_jobs' && (
            <form onSubmit={handleCompleteJob}>
              <div className="detail-section">
                <div className="detail-section-title">Approved Quote</div>
                <div className="quote-card" style={{ marginBottom: 24 }}>
                  <div className="quote-amount">£{defect.quote?.amount.toLocaleString()}</div>
                  <div className="quote-description">{defect.quote?.description}</div>
                  {defect.quote?.estimatedCompletionDate && (
                    <div className="quote-meta" style={{ marginTop: 4 }}>Estimated completion: {defect.quote.estimatedCompletionDate}</div>
                  )}
                  <div className="quote-status-banner approved">
                    <span className="status-icon">✓</span>
                    Quote approved - Ready for work
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <div className="detail-section-title">Job Completion</div>
                <div className="form-group">
                  <label className="form-label">Completion Notes *</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Describe the work completed, any issues encountered, follow-up recommendations..."
                    value={completionNotes}
                    onChange={(e) => setCompletionNotes(e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Completion Photos</label>
                  <div 
                    className={`image-upload-zone ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={(e) => handleFiles(e.target.files)}
                      style={{ display: 'none' }}
                    />
                    <div className="upload-icon">
                      <Icons.Camera />
                    </div>
                    <div className="upload-text">
                      <span className="upload-text-primary">Click to upload</span> or drag and drop
                    </div>
                    <div className="upload-hint">Add photos of completed work</div>
                  </div>
                  
                  {completionImages.length > 0 && (
                    <div className="image-preview-grid">
                      {completionImages.map(image => (
                        <div key={image.id} className="image-preview-item">
                          <img src={image.url} alt={image.name} className="image-preview-img" />
                          <div className="image-preview-overlay">
                            <button 
                              type="button"
                              className="image-remove-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeImage(image.id);
                              }}
                            >
                              <Icons.Close />
                            </button>
                          </div>
                          <div className="image-preview-info">
                            <span className="image-preview-name">{image.name}</span>
                            <span className="image-preview-size">{formatFileSize(image.size)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="action-buttons">
                <button type="submit" className="approve-btn" style={{ flex: 'none', minWidth: 200 }}>
                  <Icons.Check /> Mark Job Complete
                </button>
              </div>
            </form>
          )}

          {/* Completed View */}
          {activeTab === 'completed' && (
            <>
              <div className="detail-section">
                <div className="detail-section-title">Quote</div>
                <div className="quote-card">
                  <div className="quote-amount">£{defect.quote?.amount.toLocaleString()}</div>
                  <div className="quote-description">{defect.quote?.description}</div>
                </div>
              </div>
              {defect.completionNotes && (
                <div className="detail-section">
                  <div className="detail-section-title">Completion Notes</div>
                  <div className="detail-description">{defect.completionNotes}</div>
                </div>
              )}
              {defect.completionImages && defect.completionImages.length > 0 && (
                <div className="detail-section">
                  <div className="detail-section-title">Completion Photos</div>
                  <div className="detail-images-grid">
                    {defect.completionImages.map((image, index) => (
                      <div key={image.id || index} className="detail-image-item">
                        <img src={image.url} alt={image.name || `Image ${index + 1}`} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Toast Notification
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="toast">
      <div className={`toast-icon ${type}`}>
        {type === 'success' ? <Icons.Check /> : '!'}
      </div>
      <span className="toast-message">{message}</span>
    </div>
  );
};

// ============================================================================
// MAIN APP
// ============================================================================

export default function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [defects, setDefects] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [stores, setStores] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [toast, setToast] = useState(null);
  const [dataLoading, setDataLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Approvers state - load from D1 database
  const [approvers, setApprovers] = useState([]);
  const [approversLoading, setApproversLoading] = useState(true);

  // Load all data from D1 database when logged in
  useEffect(() => {
    const fetchAllData = async () => {
      if (!isLoggedIn) return;
      
      setDataLoading(true);
      try {
        // Fetch each list independently so one slow or failed request
        // (e.g. defects) can't stop stores, contractors or approvers loading
        const load = async (url, setter) => {
          const res = await fetch(url);
          if (!res.ok) throw new Error(`${url} returned ${res.status}`);
          setter(await res.json());
        };

        const results = await Promise.allSettled([
          load('/api/stores', setStores),
          load('/api/classifications', setClassifications),
          load('/api/approvers', setApprovers),
          load('/api/contractors', setContractors),
          load('/api/defects', setDefects),
        ]);

        const failed = results.filter(r => r.status === 'rejected');
        if (failed.length > 0) {
          failed.forEach(r => console.error('Failed to load data:', r.reason));
          setToast({ message: 'Some data failed to load - please refresh', type: 'error' });
        }
      } finally {
        setDataLoading(false);
        setApproversLoading(false);
      }
    };
    
    fetchAllData();
  }, [isLoggedIn]);

  const handleAddApprover = async (approver) => {
    try {
      const response = await fetch('/api/approvers', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: approver.name, email: approver.email })
      });
      
      if (response.ok) {
        const newApprover = await response.json();
        setApprovers([...approvers, newApprover]);
        setToast({ message: `${approver.name} added as approver`, type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to add approver', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to add approver:', error);
      setToast({ message: 'Failed to add approver', type: 'error' });
    }
  };

  const handleRemoveApprover = async (approverId) => {
    const approver = approvers.find(a => a.id === approverId);
    try {
      const response = await fetch(`/api/approvers?id=${approverId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setApprovers(approvers.filter(a => a.id !== approverId));
        setToast({ message: `${approver?.name || 'Approver'} removed`, type: 'success' });
      } else {
        setToast({ message: 'Failed to remove approver', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to remove approver:', error);
      setToast({ message: 'Failed to remove approver', type: 'error' });
    }
  };

  const handleAddContractor = async (contractor) => {
    try {
      const response = await fetch('/api/contractors', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contractor)
      });
      
      if (response.ok) {
        const newContractor = await response.json();
        setContractors([...contractors, newContractor]);
        setToast({ message: `${contractor.name} added as contractor`, type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to add contractor', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to add contractor:', error);
      setToast({ message: 'Failed to add contractor', type: 'error' });
    }
  };

  const handleRemoveContractor = async (contractorId) => {
    const contractor = contractors.find(c => c.id === contractorId);
    try {
      const response = await fetch(`/api/contractors?id=${contractorId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setContractors(contractors.filter(c => c.id !== contractorId));
        setToast({ message: `${contractor?.name || 'Contractor'} removed`, type: 'success' });
      } else {
        setToast({ message: 'Failed to remove contractor', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to remove contractor:', error);
      setToast({ message: 'Failed to remove contractor', type: 'error' });
    }
  };

  const handleEditContractor = async (contractorId, updates) => {
    try {
      const response = await fetch(`/api/contractors?id=${contractorId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        const updatedContractor = await response.json();
        setContractors(contractors.map(c =>
          c.id === contractorId ? updatedContractor : c
        ));
        setToast({ message: 'Contractor updated', type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to update contractor', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to update contractor:', error);
      setToast({ message: 'Failed to update contractor', type: 'error' });
    }
  };

  const handleAddStore = async (store) => {
    try {
      const response = await fetch('/api/stores', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(store)
      });
      
      if (response.ok) {
        const newStore = await response.json();
        setStores([...stores, newStore]);
        setToast({ message: `${store.name} added as store`, type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to add store', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to add store:', error);
      setToast({ message: 'Failed to add store', type: 'error' });
    }
  };

  const handleRemoveStore = async (storeId) => {
    const store = stores.find(s => s.id === storeId);
    try {
      const response = await fetch(`/api/stores?id=${storeId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setStores(stores.filter(s => s.id !== storeId));
        setToast({ message: `${store?.name || 'Store'} removed`, type: 'success' });
      } else {
        setToast({ message: 'Failed to remove store', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to remove store:', error);
      setToast({ message: 'Failed to remove store', type: 'error' });
    }
  };

  const handleAddClassification = async (classification) => {
    try {
      const response = await fetch('/api/classifications', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(classification)
      });
      
      if (response.ok) {
        const newClassification = await response.json();
        setClassifications([
          ...classifications.filter(c => c.id !== newClassification.id),
          newClassification
        ].sort((a, b) => a.name.localeCompare(b.name)));
        setToast({ message: `${newClassification.name} added as classification`, type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to add classification', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to add classification:', error);
      setToast({ message: 'Failed to add classification', type: 'error' });
    }
  };

  const handleRemoveClassification = async (classificationId) => {
    const classification = classifications.find(c => c.id === classificationId);
    try {
      const response = await fetch(`/api/classifications?id=${classificationId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        // Keep it in the list as removed so existing defects keep their label
        setClassifications(classifications.map(c =>
          c.id === classificationId ? { ...c, active: 0 } : c
        ));
        setToast({ message: `${classification?.name || 'Classification'} removed`, type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to remove classification', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to remove classification:', error);
      setToast({ message: 'Failed to remove classification', type: 'error' });
    }
  };

  // Handle OAuth callback and token exchange
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      // Check for errors from Microsoft
      if (error) {
        setAuthError(errorDescription || error);
        setIsLoading(false);
        // Clean URL
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }
      
      // Check if we have an auth code to exchange
      if (code && state) {
        const storedState = sessionStorage.getItem('auth_state');
        const codeVerifier = sessionStorage.getItem('pkce_verifier');
        
        // Validate state to prevent CSRF
        if (state !== storedState) {
          setAuthError('Invalid authentication state. Please try again.');
          setIsLoading(false);
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
        
        try {
          // Exchange code for tokens
          const tokenResponse = await fetch(
            `https://login.microsoftonline.com/${msalConfig.tenantId}/oauth2/v2.0/token`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                client_id: msalConfig.clientId,
                grant_type: 'authorization_code',
                code: code,
                redirect_uri: msalConfig.redirectUri,
                code_verifier: codeVerifier,
                scope: msalConfig.scopes.join(' ')
              })
            }
          );
          
          if (!tokenResponse.ok) {
            const errorData = await tokenResponse.json();
            throw new Error(errorData.error_description || 'Failed to exchange code for token');
          }
          
          const tokens = await tokenResponse.json();
          
          // Store tokens
          sessionStorage.setItem('access_token', tokens.access_token);
          if (tokens.refresh_token) {
            sessionStorage.setItem('refresh_token', tokens.refresh_token);
          }
          
          // Get user info from Microsoft Graph
          const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
            headers: {
              'Authorization': `Bearer ${tokens.access_token}`
            }
          });
          
          if (!userResponse.ok) {
            throw new Error('Failed to get user info');
          }
          
          const userData = await userResponse.json();
          
          await createApiSession(tokens.access_token);
          
          // Set user and login state
          setUser({
            name: userData.displayName || userData.givenName + ' ' + userData.surname,
            email: userData.mail || userData.userPrincipalName,
            role: 'Facility Manager' // You can customize this based on Azure AD groups
          });
          setIsLoggedIn(true);
          
          // Clean up
          sessionStorage.removeItem('pkce_verifier');
          sessionStorage.removeItem('auth_state');
          window.history.replaceState({}, document.title, window.location.pathname);
          
        } catch (err) {
          console.error('Token exchange error:', err);
          setAuthError(err.message || 'Authentication failed. Please try again.');
        }
      } else {
        // Check if we have an existing session
        const accessToken = sessionStorage.getItem('access_token');
        if (accessToken) {
          try {
            // Validate token by fetching user info
            const userResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
              headers: {
                'Authorization': `Bearer ${accessToken}`
              }
            });
            
            if (userResponse.ok) {
              const userData = await userResponse.json();
              await createApiSession(accessToken);
              setUser({
                name: userData.displayName || userData.givenName + ' ' + userData.surname,
                email: userData.mail || userData.userPrincipalName,
                role: 'Facility Manager'
              });
              setIsLoggedIn(true);
            } else {
              // Token expired, clear it
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('refresh_token');
            }
          } catch (err) {
            console.error('Session validation error:', err);
            sessionStorage.removeItem('access_token');
            sessionStorage.removeItem('refresh_token');
          }
        }
      }
      
      setIsLoading(false);
    };
    
    handleAuthCallback();
  }, []);

  const handleLogout = () => {
    // Clear local session and the API session cookie
    fetch('/api/session', { method: 'DELETE', keepalive: true }).catch(() => {});
    sessionStorage.removeItem('access_token');
    sessionStorage.removeItem('refresh_token');
    setIsLoggedIn(false);
    setUser(null);
    setCurrentPage('dashboard');
    
    // Redirect to Microsoft logout
    const logoutUrl = new URL(`https://login.microsoftonline.com/${msalConfig.tenantId}/oauth2/v2.0/logout`);
    logoutUrl.searchParams.append('post_logout_redirect_uri', msalConfig.redirectUri);
    window.location.href = logoutUrl.toString();
  };

  const handleAddDefect = async (data) => {
    try {
      const response = await fetch('/api/defects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: data.title,
          description: data.description,
          store: data.store,
          severity: data.severity,
          classification: data.classification,
          reporterName: data.reporterName,
          reportedByName: user.name,
          reportedByEmail: user.email,
          images: data.images || []
        })
      });
      
      if (response.ok) {
        const newDefect = await response.json();
        // Transform to frontend format
        const transformedDefect = {
          id: newDefect.id,
          title: newDefect.title,
          description: newDefect.description,
          location: newDefect.store,
          severity: newDefect.severity,
          classification: newDefect.classification,
          reporterName: newDefect.reporter_name,
          reportedBy: newDefect.reported_by_name,
          reportedByEmail: newDefect.reported_by_email,
          reportedDate: newDefect.reported_date,
          status: newDefect.status,
          images: (newDefect.images || []).map(i => ({
            id: i.id,
            name: i.filename || i.name,
            url: i.storage_key || i.url,
            type: i.content_type || i.type
          })),
          contractor: null,
          quote: null,
          approvals: []
        };
        setDefects([transformedDefect, ...defects]);
        setToast({ message: 'Defect reported successfully', type: 'success' });
        
        // Send email notification
        sendNotification('defect_created', transformedDefect);
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to create defect', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to create defect:', error);
      setToast({ message: 'Failed to create defect', type: 'error' });
    }
  };

  // Helper function to send email notifications
  const sendNotification = async (type, defect) => {
    try {
      const response = await fetch('/api/notify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          defect,
          submitterEmail: user?.email,
          approverEmails: approvers.map(a => a.email)
        })
      });
      const result = await response.json();
      if (result.success) {
        console.log(`Notification sent: ${result.message}`);
      } else {
        console.error('Notification failed:', result.error);
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
    }
  };

  const handleApprove = async (defectId, approvalType, contractor = null) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          approvalType,
          approvedByName: user.name,
          contractorId: contractor?.id || null
        })
      });
      
      if (response.ok) {
        const result = await response.json();
        
        // Update local state
        const updatedDefect = defects.find(d => d.id === defectId);
        if (updatedDefect) {
          const newApprovals = [...(updatedDefect.approvals || []), {
            type: approvalType,
            approvedBy: user.name,
            date: new Date().toISOString().split('T')[0]
          }];
          
          // Use status from backend response
          let newStatus = result.newStatus || updatedDefect.status;
          if (!result.newStatus) {
            // Fallback if backend doesn't return newStatus
            if (approvalType === 'defect') {
              newStatus = 'contractor_assigned';
            } else if (approvalType === 'quote' || approvalType === 'bypass') {
              newStatus = 'quote_approved';
            }
          }
          
          const updated = { 
            ...updatedDefect, 
            approvals: newApprovals, 
            status: newStatus,
            contractor: contractor || updatedDefect.contractor
          };
          setDefects(defects.map(d => d.id === defectId ? updated : d));
          
          // Send notification for defect approval only
          // Backend handles quote_approved notification to contractor
          if (approvalType === 'defect' || approvalType === 'bypass') {
            sendNotification('defect_approved', updated);
          }
        }
        
        setSelectedDefect(null);
        setToast({ 
          message: approvalType === 'defect' 
            ? `Defect approved - contractor ${contractor?.name} assigned and notified` 
            : approvalType === 'bypass'
            ? `Work approved - contractor ${contractor?.name} can proceed`
            : 'Quote approved - contractor notified to proceed with work',
          type: 'success' 
        });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to approve', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to approve:', error);
      setToast({ message: 'Failed to approve', type: 'error' });
    }
  };

  const handleAssignContractor = async (defectId, contractor) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/assign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractorId: contractor.id
        })
      });

      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return { ...d, contractor };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast({
          message: `Contractor assigned - email sent to ${contractor.email}`,
          type: 'success'
        });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to assign contractor', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to assign contractor:', error);
      setToast({ message: 'Failed to assign contractor', type: 'error' });
    }
  };

  const handleReassignContractor = async (defectId, contractor) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/reassign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contractorId: contractor.id
        })
      });

      if (response.ok) {
        // Refresh defects list to get updated data
        const defectsResponse = await fetch('/api/defects');
        if (defectsResponse.ok) {
          const defectsData = await defectsResponse.json();
          // /api/defects already returns the frontend format
          setDefects(defectsData);
        }
        setSelectedDefect(null);
        setToast({
          message: `Defect reassigned to ${contractor.name} - notification sent`,
          type: 'success'
        });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to reassign contractor', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to reassign contractor:', error);
      setToast({ message: 'Failed to reassign contractor', type: 'error' });
    }
  };

  const handleSendReminder = async (defectId) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/remind`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        setToast({
          message: result.message || 'Reminder sent successfully',
          type: 'success'
        });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to send reminder', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to send reminder:', error);
      setToast({ message: 'Failed to send reminder', type: 'error' });
    }
  };

  const handleManualComplete = async (defectId) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: `Manually completed by ${user.name}`,
          images: []
        })
      });

      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return {
              ...d,
              status: 'completed',
              completionNotes: `Manually completed by ${user.name}`,
              completedDate: new Date().toISOString().split('T')[0]
            };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast({
          message: 'Job marked as complete',
          type: 'success'
        });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to complete job', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to complete job:', error);
      setToast({ message: 'Failed to complete job', type: 'error' });
    }
  };

  const handleSubmitQuote = async (defectId, quote) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: quote.amount,
          description: quote.description,
          estimatedCompletionDate: quote.estimatedCompletionDate
        })
      });
      
      if (response.ok) {
        const defect = defects.find(d => d.id === defectId);
        const updatedDefect = { ...defect, quote, status: 'quote_received' };
        
        setDefects(defects.map(d => d.id === defectId ? updatedDefect : d));
        setToast({ message: 'Quote submitted successfully', type: 'success' });
        
        // Send notification to approvers
        sendNotification('quote_submitted', updatedDefect);
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to submit quote', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to submit quote:', error);
      setToast({ message: 'Failed to submit quote', type: 'error' });
    }
  };

  const handleReject = async (defectId, reason) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/reject`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          reason,
          rejectedByName: user.name
        })
      });
      
      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return { 
              ...d, 
              status: 'rejected',
              rejectionReason: reason,
              rejectedBy: user.name,
              rejectedDate: new Date().toISOString().split('T')[0]
            };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast({ message: 'Defect/Quote rejected - notification sent', type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to reject', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to reject:', error);
      setToast({ message: 'Failed to reject', type: 'error' });
    }
  };

  const handleRequestRevision = async (defectId, comments) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/revision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comments,
          requestedByName: user.name
        })
      });
      
      if (response.ok) {
        setDefects(defects.map(d => {
          if (d.id === defectId) {
            return { 
              ...d, 
              status: 'revision_requested',
              revisionComments: comments,
              revisionRequestedBy: user.name,
              revisionRequestedDate: new Date().toISOString().split('T')[0]
            };
          }
          return d;
        }));
        setSelectedDefect(null);
        setToast({ message: 'Revision requested - contractor notified', type: 'success' });
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to request revision', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to request revision:', error);
      setToast({ message: 'Failed to request revision', type: 'error' });
    }
  };

  const handleCompleteJob = async (defectId, completion) => {
    try {
      const response = await fetch(`/api/defects/${defectId}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          notes: completion.notes,
          images: completion.images
        })
      });
      
      if (response.ok) {
        const defect = defects.find(d => d.id === defectId);
        const updatedDefect = { 
          ...defect, 
          status: 'completed',
          completionNotes: completion.notes,
          completionImages: completion.images,
          completedDate: new Date().toISOString().split('T')[0]
        };
        
        setDefects(defects.map(d => d.id === defectId ? updatedDefect : d));
        setToast({ message: 'Job marked as complete', type: 'success' });
        
        // Send notification to submitter and approvers
        sendNotification('job_completed', updatedDefect);
      } else {
        const error = await response.json();
        setToast({ message: error.error || 'Failed to complete job', type: 'error' });
      }
    } catch (error) {
      console.error('Failed to complete job:', error);
      setToast({ message: 'Failed to complete job', type: 'error' });
    }
  };

  const pendingCount = defects.filter(d => 
    d.status === 'pending_approval' || d.status === 'quote_received'
  ).length;

  // Check if current user is an approver (includes hardcoded admin)
  const isApprover = user && (
    user.email?.toLowerCase() === 'snapit@gouldbarbers.co.uk' ||
    user.email?.toLowerCase() === 'peter@gouldbarbers.co.uk' ||
    approvers.some(a => a.email.toLowerCase() === user.email?.toLowerCase())
  );

  // Filter defects for non-approvers to only show their own
  const visibleDefects = isApprover 
    ? defects 
    : defects.filter(d => d.reportedBy === user?.name || d.reportedByEmail === user?.email);

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
      case 'defects':
        return (
          <DefectsPage 
            defects={visibleDefects} 
            onAddDefect={handleAddDefect}
            onSelectDefect={setSelectedDefect}
            stores={stores}
          />
        );
      case 'approvals':
        return isApprover ? <ApprovalsPage defects={defects} onSelectDefect={setSelectedDefect} /> : <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
      case 'contractors':
        return isApprover ? <ContractorsPage contractors={contractors} onAddContractor={handleAddContractor} onRemoveContractor={handleRemoveContractor} onEditContractor={handleEditContractor} /> : <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
      case 'reports':
        return isApprover ? <ReportsPage defects={defects} stores={stores} /> : <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
      case 'settings':
        return isApprover ? <SettingsPage approvers={approvers} onAddApprover={handleAddApprover} onRemoveApprover={handleRemoveApprover} stores={stores} onAddStore={handleAddStore} onRemoveStore={handleRemoveStore} onAddClassification={handleAddClassification} onRemoveClassification={handleRemoveClassification} /> : <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
      default:
        return <DashboardPage defects={visibleDefects} setCurrentPage={setCurrentPage} onSelectDefect={setSelectedDefect} onAddDefect={handleAddDefect} stores={stores} />;
    }
  };

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <>
        <style>{styles}</style>
        <div className="login-container">
          <div className="login-card" style={{ textAlign: 'center' }}>
            <div className="login-logo">
              <div className="login-logo-icon">
                <Icons.Logo />
              </div>
              <span className="login-logo-text">DefectTrack</span>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginTop: 24 }}>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  if (!isLoggedIn) {
    return (
      <>
        <style>{styles}</style>
        <LoginScreen isLoading={isLoading} error={authError} />
      </>
    );
  }

  return (
    <ClassificationsContext.Provider value={classifications}>
      <style>{styles}</style>
      <div className="app-layout">
        <div className="mobile-header">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)}>
            <Icons.Menu />
          </button>
          <div className="mobile-header-logo">
            <div className="sidebar-logo-icon" style={{ width: 28, height: 28, fontSize: 11 }}>
              <Icons.Logo />
            </div>
            <span className="sidebar-logo-text" style={{ fontSize: 14 }}>DefectTrack</span>
          </div>
        </div>
        <Sidebar
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          user={user}
          onLogout={handleLogout}
          pendingCount={pendingCount}
          isApprover={isApprover}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className="main-content">
          {renderPage()}
        </main>
      </div>

      {selectedDefect && (
        <DefectDetailModal
          defect={selectedDefect}
          onClose={() => setSelectedDefect(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onRequestRevision={handleRequestRevision}
          onAssignContractor={handleAssignContractor}
          onReassignContractor={handleReassignContractor}
          onSendReminder={handleSendReminder}
          onManualComplete={handleManualComplete}
          contractors={contractors}
          isApprover={isApprover}
        />
      )}

      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </ClassificationsContext.Provider>
  );
}
